<template>
  <div class="dashboard-master">
    <!-- Header -->
    <div class="dm-header mb-0">
      <h2 class="dm-title mb-0">Dashboard</h2>
    </div>

    <!-- Barra de filtros -->
    <div class="dm-toolbar">
      <div class="dm-toolbar__left">
        <span class="dm-toolbar__label">{{ $t('dm_empresa_label') }}</span>
        <select
          v-if="isAdmin"
          v-model="idEmpresaSeleccionada"
          class="dm-select-native"
          @change="onEmpresaChange"
        >
          <option :value="0">{{ $t('dm_todas') }}</option>
          <option
            v-for="c in clientesResumen"
            :key="c.idCliente"
            :value="c.idCliente"
          >{{ c.nombreCliente }}</option>
        </select>
        <span v-else class="badge badge-primary px-3 text-uppercase" style="display:inline-flex;align-items:center;height:32px;font-size:.85rem;">{{ nombreEmpresaCliente }}</span>

        <!-- Ahorro Potencial inline -->
        <span v-if="kpis && kpis.ajustePreciosSugerido < 0" class="dm-ahorro-inline">
          {{ $t('dm_ahorro_potencial', { pct: Math.abs(Math.round(kpis.ajustePreciosSugerido / kpis.stockTotalValorizado * 1000) / 10) }) }}
        </span>
      </div>
      <div class="d-flex align-items-center" style="gap:8px;">
        <span v-if="consolidacionEstado && consolidacionEstado.ultimaEjecucion"
              class="text-muted" style="font-size:.75rem;white-space:nowrap;">
          {{ $t('dm_datos_al') }} {{ formatoFechaHora(consolidacionEstado.ultimaEjecucion) }}
        </span>
        <b-button v-if="puedeConsolidar" size="sm" variant="outline-primary" :disabled="consolidando || loadingAny"
                  :title="$t('dm_consolidar_tooltip')" @click="consolidarDatos">
          <i class="fas fa-database mr-1" :class="{ 'fa-spin': consolidando }"></i>
          {{ $t('dm_consolidar') }}
        </b-button>
        <b-button size="sm" variant="outline-secondary" :disabled="loadingAny" @click="loadAll">
          <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loadingAny }"></i>
          {{ $t('dm_actualizar') }}
        </b-button>
      </div>
    </div>

    <!-- Barra de Tabs -->
    <div class="dm-tab-bar">
      <div class="dm-tab-bar__inner">
        <button @click="activeTab = 'resumen'" :class="['dm-tab-btn', activeTab === 'resumen' ? 'dm-tab-btn--active' : '']">
          {{ $t('dm_tab_kpis') }}
        </button>
        <button
          v-if="idEmpresaSeleccionada === 0 && isAdmin"
          @click="activeTab = 'clasificacion'"
          :class="['dm-tab-btn', activeTab === 'clasificacion' ? 'dm-tab-btn--active' : '']"
        >
          {{ $t('dm_tab_clasificacion') }}
        </button>
        <button
          @click="activeTab = 'cronologia'"
          :class="['dm-tab-btn', activeTab === 'cronologia' ? 'dm-tab-btn--active' : '']"
        >
          {{ $t('dm_tab_cronologia') }}
        </button>
        <button
          @click="activeTab = 'monitoreo'"
          :class="['dm-tab-btn', activeTab === 'monitoreo' ? 'dm-tab-btn--active' : '']"
        >
          {{ $t('dm_tab_monitoreo') }}
        </button>
        <button
          @click="activeTab = 'rfm'"
          :class="['dm-tab-btn', activeTab === 'rfm' ? 'dm-tab-btn--active' : '']"
        >{{ $t('dm_tab_fidelidad') }}</button>
        <button class="dm-tab-btn dm-tab-btn--soon" disabled>{{ $t('dm_tab_auditoria') }} <span class="dm-tab-soon-badge">{{ $t('dm_tab_soon') }}</span></button>
        <button class="dm-tab-btn dm-tab-btn--soon" disabled>{{ $t('dm_tab_tabla') }} <span class="dm-tab-soon-badge">{{ $t('dm_tab_soon') }}</span></button>
      </div>
    </div>

    <!-- KPI Row 1: cards compactos con icono -->
    <div v-show="activeTab === 'resumen'" class="kpi-secondary-grid mb-3">
      <div v-for="kpi in kpiCardsSecundarios" :key="kpi.key" class="kpi-secondary-card">
        <div class="kpi-s-icon" :style="{ background: kpi.iconBg }">
          <i :class="kpi.icon" :style="{ color: kpi.iconColor }" />
        </div>
        <div class="kpi-s-body">
          <div class="kpi-s-label">{{ kpi.label }}</div>
          <div class="kpi-s-value">{{ kpi.value }}</div>
          <div class="kpi-s-sub">{{ kpi.sub }}</div>
        </div>
      </div>
    </div>

    <!-- KPI Row 2: cards primarios con descripción -->
    <div v-show="activeTab === 'resumen'" class="kpi-primary-grid mb-4">
      <div
        v-for="kpi in kpiCardsPrimarios"
        :key="kpi.key"
        class="kpi-primary-card"
      >
        <i v-if="kpi.icon" :class="['kpi-watermark', kpi.icon]" />
        <div class="kpi-p-label">{{ kpi.label }}</div>
        <div class="kpi-p-value">{{ kpi.value }}</div>
        <div v-if="kpi.sub" class="kpi-p-sub" :class="kpi.subClass">{{ kpi.sub }}</div>
        <hr class="kpi-divider" />
        <div class="kpi-section-label">{{ kpi.sectionLabel }}</div>
        <p class="kpi-description">{{ kpi.description }}</p>
      </div>
    </div>

    <!-- Contenido de Tabs -->
    <div class="dm-tab-content">

      <!-- PANEL: Resumen y KPIs -->
      <div v-show="activeTab === 'resumen'" class="dm-tab-panel">

        <!-- ══ VISTA TODOS LOS CLIENTES (admin) ══════════════════════ -->
        <template v-if="idEmpresaSeleccionada === 0 || !idEmpresaSeleccionada">
        <div v-if="loadingClientes" class="dm-loading"><i class="el-icon-loading" /> {{ $t('dm_cargando') }}</div>
        <div v-else class="charts-grid-resumen">
          <!-- Comparativa clientes -->
          <div class="chart-card chart-card--featured chart-card--wide" v-if="isAdmin">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_clientes_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_clientes_sub') }}</div>
              </div>
              <span class="chart-badge">{{ $t('dm_chart_clientes_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--lg"><canvas ref="chartClientes"></canvas></div>
          </div>
          <!-- Resumen de Negocio por Cliente -->
          <div class="chart-card chart-card--featured" v-if="isAdmin">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_negocio_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_negocio_sub') }}</div>
              </div>
            </div>
            <div style="overflow-x:auto">
              <table class="ranking-table" style="min-width:520px">
                <thead>
                  <tr>
                    <th>{{ $t('dm_col_cliente') }}</th>
                    <th class="rt-col-num" v-html="$t('dm_col_items_pub_h')"></th>
                    <th class="rt-col-num" v-html="$t('dm_col_items_carg_h')"></th>
                    <th class="rt-col-num">{{ $t('dm_col_vendido') }}</th>
                    <th class="rt-col-num">{{ $t('dm_col_items_pub_usd') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in clientesOrdenados" :key="c.nombreCliente">
                    <td class="rt-nombre">{{ c.nombreCliente }}</td>
                    <td class="rt-match">{{ (c.itemsPublicados || 0).toLocaleString(numLocale()) }}</td>
                    <td class="rt-conf">{{ (c.totalItems || 0).toLocaleString(numLocale()) }}</td>
                    <td class="rt-match">{{ formatUSD(c.vendidoMonto) }}</td>
                    <td class="rt-match" style="font-size:0.82rem">{{ formatUSD(c.valorTotal) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Fila 1b: Evolución Temporal de Ítems Vendidos + Estado Publicación -->
        <div class="charts-grid-resumen mt-3" v-if="!loadingClientes && isAdmin">
          <div class="chart-card chart-card--featured chart-card--wide">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_ventas_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_ventas_sub') }}</div>
              </div>
              <span class="chart-badge">{{ $t('dm_chart_ventas_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--lg"><canvas ref="chartVentasTimeline"></canvas></div>
          </div>
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_est_pub_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_est_pub_sub') }}</div>
              </div>
              <span class="chart-badge">{{ $t('dm_chart_est_pub_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--donut"><canvas ref="chartEstadoPublicacion"></canvas></div>
          </div>
        </div>

        <!-- Fila 2: distribuciones financiera/cantidad + match rate -->
        <div class="charts-grid-3 mt-3" v-if="!loadingClientes && isAdmin">
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_dist_fin_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_dist_fin_sub') }}</div>
              </div>
              <span class="chart-badge chart-badge--blue">{{ $t('dm_chart_dist_fin_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--donut"><canvas ref="chartDonutValor"></canvas></div>
          </div>
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_dist_cant_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_dist_cant_sub') }}</div>
              </div>
              <span class="chart-badge chart-badge--pink">{{ $t('dm_chart_dist_cant_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--donut"><canvas ref="chartDonutCantidad"></canvas></div>
          </div>
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_match_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_match_sub') }}</div>
              </div>
              <span class="chart-badge chart-badge--green">{{ $t('dm_chart_match_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--donut"><canvas ref="chartMatchRate"></canvas></div>
          </div>
        </div>

        <!-- Fila 3: clase + confianza cliente + top fabricantes -->
        <div class="charts-grid-3 mt-3" v-if="!loadingClases && !loadingMarcas && isAdmin">
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_clase_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_clase_sub') }}</div>
              </div>
              <span class="chart-badge chart-badge--orange">{{ $t('dm_chart_clase_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--donut"><canvas ref="chartClase"></canvas></div>
          </div>
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_confianza_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_confianza_sub') }}</div>
              </div>
              <span class="chart-badge chart-badge--blue">{{ $t('dm_chart_confianza_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--donut"><canvas ref="chartConfianzaCliente"></canvas></div>
          </div>
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('dm_chart_top_fab_title') }}</div>
                <div class="chart-subtitle">{{ $t('dm_chart_top_fab_sub') }}</div>
              </div>
              <span class="chart-badge chart-badge--pink">{{ $t('dm_chart_top_fab_badge') }}</span>
            </div>
            <div class="chart-wrap chart-wrap--donut"><canvas ref="chartMarcas"></canvas></div>
          </div>
        </div>

        </template>

        <!-- ══ VISTA CLIENTE ESPECÍFICO ══════════════════════════════ -->
        <template v-else>
          <div v-if="loadingCategorias || loadingClases || loadingMarcas" class="dm-loading"><i class="el-icon-loading" /> {{ $t('dm_cargando') }}</div>
          <div v-else>

            <!-- Fila 1 cliente: Distribución de valor + Estado publicación -->
            <div class="charts-grid-2 mb-3">
              <div class="chart-card chart-card--featured">
                <div class="chart-header">
                  <div>
                    <div class="chart-title chart-title--lg">{{ $t('dm_chart_client_valor_title') }}</div>
                    <div class="chart-subtitle">{{ $t('dm_chart_client_valor_sub') }}</div>
                  </div>
                  <span class="chart-badge chart-badge--blue">{{ $t('dm_chart_client_valor_badge') }}</span>
                </div>
                <div class="chart-wrap chart-wrap--donut"><canvas ref="chartClienteValorCat"></canvas></div>
              </div>
              <div class="chart-card chart-card--featured">
                <div class="chart-header">
                  <div>
                    <div class="chart-title chart-title--lg">{{ $t('dm_chart_client_cat_title') }}</div>
                    <div class="chart-subtitle">{{ $t('dm_chart_client_cat_sub') }}</div>
                  </div>
                  <span class="chart-badge chart-badge--green">{{ $t('dm_chart_client_cat_badge') }}</span>
                </div>
                <div class="chart-wrap chart-wrap--donut"><canvas ref="chartClienteCatalogacion"></canvas></div>
              </div>
            </div>

            <!-- Fila 2 cliente: Top 5 Fabricantes + Clase + Ahorro -->
            <div class="charts-grid-3">
              <div class="chart-card chart-card--featured">
                <div class="chart-header">
                  <div>
                    <div class="chart-title chart-title--lg">{{ $t('dm_chart_client_fab_title') }}</div>
                    <div class="chart-subtitle">{{ $t('dm_chart_client_fab_sub') }}</div>
                  </div>
                  <span class="chart-badge chart-badge--pink">{{ $t('dm_chart_client_fab_badge') }}</span>
                </div>
                <div class="chart-wrap chart-wrap--donut"><canvas ref="chartClienteMarcas"></canvas></div>
              </div>
              <div class="chart-card chart-card--featured">
                <div class="chart-header">
                  <div>
                    <div class="chart-title chart-title--lg">{{ $t('dm_chart_client_clase_title') }}</div>
                    <div class="chart-subtitle">{{ $t('dm_chart_client_clase_sub') }}</div>
                  </div>
                  <span class="chart-badge chart-badge--orange">{{ $t('dm_chart_client_clase_badge') }}</span>
                </div>
                <div class="chart-wrap chart-wrap--donut"><canvas ref="chartClienteClase"></canvas></div>
              </div>
              <div class="chart-card chart-card--featured">
                <div class="chart-header">
                  <div>
                    <div class="chart-title chart-title--lg">{{ $t('dm_chart_client_ahorro_title') }}</div>
                    <div class="chart-subtitle">{{ $t('dm_chart_client_ahorro_sub') }}</div>
                  </div>
                  <span class="chart-badge chart-badge--green">{{ $t('dm_chart_client_ahorro_badge') }}</span>
                </div>
                <div class="chart-wrap chart-wrap--donut"><canvas ref="chartClienteAhorro"></canvas></div>
              </div>
            </div>

          </div>
        </template>

      </div>

      <!-- PANEL: Cronología e Ingesta -->
      <div v-show="activeTab === 'cronologia'" class="dm-tab-panel">
        <div v-if="loadingTimeline" class="dm-loading"><i class="el-icon-loading" /> {{ $t('tl_loading') }}</div>
        <div v-else>

          <!-- Gráficos: Volumen y Valor -->
          <div class="charts-grid-2 mb-3">
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('tl_vol_title') }}</div>
                  <div class="chart-subtitle">{{ $t('tl_vol_sub') }}</div>
                </div>
                <span class="chart-badge">{{ $t('tl_vol_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--tall"><canvas ref="chartTimelineVolume"></canvas></div>
            </div>
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('tl_val_title') }}</div>
                  <div class="chart-subtitle">{{ $t('tl_val_sub') }}</div>
                </div>
                <span class="chart-badge chart-badge--green">{{ $t('tl_val_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--tall"><canvas ref="chartTimelineValue"></canvas></div>
            </div>
          </div>

          <!-- Tabla: Archivos de Origen Cargados (Tokens) -->
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('tl_tokens_title') }}</div>
                <div class="chart-subtitle">{{ $t('tl_tokens_sub') }}</div>
              </div>
            </div>
            <div style="overflow-x:auto">
              <table class="ranking-table" style="min-width:900px">
                <thead>
                  <tr>
                    <th style="min-width:200px">{{ $t('tl_th_archivo') }}</th>
                    <th>{{ $t('tl_th_cliente') }}</th>
                    <th class="rt-col-num">{{ $t('tl_th_fecha') }}</th>
                    <th class="rt-col-num">{{ $t('tl_th_registros') }}</th>
                    <th class="rt-col-num">{{ $t('tl_th_valor') }}</th>
                    <th class="rt-col-num" style="min-width:160px">{{ $t('tl_th_homologacion') }}</th>
                    <th class="rt-col-num" style="min-width:200px">{{ $t('tl_th_salud') }}</th>
                    <!-- <th class="rt-col-num">{{ $t('tl_th_accion') }}</th> -->
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in timelineTokens" :key="t.token + t.fecha" style="border-bottom:1px solid #f3f4f6">
                    <!-- Archivo Ingesta -->
                    <td style="padding:14px 12px">
                      <div style="font-weight:600;color:#2563eb;font-size:13px">{{ t.nombreXLSX || $t('tl_sin_nombre') }}</div>
                      <div style="font-size:10px;color:#9ca3af;font-family:monospace;margin-top:2px">{{ t.token.substring(0, 18) }}...</div>
                    </td>
                    <!-- Cliente -->
                    <td style="padding:14px 12px;font-size:13px">{{ t.nombreCliente }}</td>
                    <!-- Fecha -->
                    <td class="rt-col-num" style="padding:14px 12px;font-size:13px">{{ t.fecha }}</td>
                    <!-- Registros -->
                    <td class="rt-col-num" style="padding:14px 12px;font-weight:700;font-size:13px">{{ t.cantidad.toLocaleString(numLocale()) }}</td>
                    <!-- Valor Inventario -->
                    <td class="rt-col-num" style="padding:14px 12px;font-size:13px">
                      ${{ Math.round(t.valorTotal).toLocaleString(numLocale()) }} USD
                    </td>
                    <!-- Homologación -->
                    <td class="rt-col-num" style="padding:14px 12px;text-align:center">
                      <div :style="{ color: t.cantidad > 0 && t.itemsHomologados / t.cantidad >= 0.5 ? '#2563eb' : '#f59e0b', fontWeight:700, fontSize:'14px' }">
                        {{ t.cantidad > 0 ? ((t.itemsHomologados / t.cantidad) * 100).toFixed(1) : '0.0' }}%
                      </div>
                      <div style="font-size:10px;color:#9ca3af;margin-top:2px">
                        {{ t.itemsHomologados }} / {{ t.cantidad }} {{ $t('tl_items') }}
                      </div>
                    </td>
                    <!-- Salud del Archivo -->
                    <td style="padding:14px 12px;min-width:200px">
                      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px">
                        <span :style="{ fontWeight:700, fontSize:'13px', color: _saludColor(t) }">
                          {{ _saludPct(t) }}% {{ $t('tl_salud') }}
                        </span>
                        <span style="font-size:11px;color:#9ca3af">
                          {{ t.cantidad - t.criticos }} {{ $t('tl_de') }} {{ t.cantidad }}
                        </span>
                      </div>
                      <div style="height:6px;border-radius:4px;background:#e5e7eb;overflow:hidden;margin-bottom:5px">
                        <div :style="{ width: _saludPct(t) + '%', height:'100%', borderRadius:'4px', background: _saludColor(t), transition:'width 0.4s' }"></div>
                      </div>
                      <div style="display:flex;gap:4px;flex-wrap:wrap">
                        <span v-if="t.criticos > 0" style="font-size:10px;font-weight:600;padding:1px 6px;border-radius:4px;background:#fee2e2;color:#dc2626">
                          {{ t.criticos }} {{ $t('tl_critico') }}
                        </span>
                        <span v-if="t.advertencias > 0" style="font-size:10px;font-weight:600;padding:1px 6px;border-radius:4px;background:#fef3c7;color:#d97706">
                          {{ t.advertencias }} {{ $t('tl_advertencia') }}
                        </span>
                      </div>
                    </td>
                    <!-- Acción (oculta)
                    <td class="rt-col-num" style="padding:14px 12px">
                      <b-button size="sm" variant="primary" @click="filtrarPorToken(t.token)">
                        {{ $t('tl_filtrar_lote') }}
                      </b-button>
                    </td> -->
                  </tr>
                  <tr v-if="!timelineTokens.length">
                    <td colspan="7" style="text-align:center;padding:24px;color:#9ca3af">{{ $t('tl_sin_datos') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      <!-- PANEL: Clasificación e IA (solo vista Todos / admin) -->
      <div
        v-if="idEmpresaSeleccionada === 0 && isAdmin"
        v-show="activeTab === 'clasificacion'"
        class="dm-tab-panel"
      >
        <div v-if="loadingFamilias || loadingSegmentos || loadingCategorias || loadingClases || loadingConfianza" class="dm-loading">
          <i class="el-icon-loading" /> {{ $t('clasif_loading') }}
        </div>
        <div v-else>

          <!-- Descripción del panel -->
          <div class="clasif-intro mb-3">
            <div class="chart-title--lg mb-1">{{ $t('clasif_intro_title') }}</div>
            <div class="chart-subtitle">{{ $t('clasif_intro_desc') }}</div>
          </div>

          <!-- Fila 1: Categorías + Familias -->
          <div class="charts-grid-2 mb-3">
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('clasif_cat_title') }}</div>
                  <div class="chart-subtitle">{{ $t('clasif_cat_sub') }}</div>
                </div>
                <span class="chart-badge">{{ $t('clasif_cat_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--tall"><canvas ref="chartClasifCategorias"></canvas></div>
            </div>
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('clasif_fam_title') }}</div>
                  <div class="chart-subtitle">{{ $t('clasif_fam_sub') }}</div>
                </div>
                <span class="chart-badge">{{ $t('clasif_fam_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--tall"><canvas ref="chartClasifFamilias"></canvas></div>
            </div>
          </div>

          <!-- Fila 2: Clases + Segmentos -->
          <div class="charts-grid-2 mb-3">
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('clasif_clase_title') }}</div>
                  <div class="chart-subtitle">{{ $t('clasif_clase_sub') }}</div>
                </div>
                <span class="chart-badge chart-badge--orange">{{ $t('clasif_clase_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--tall"><canvas ref="chartClasifClases"></canvas></div>
            </div>
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('clasif_seg_title') }}</div>
                  <div class="chart-subtitle">{{ $t('clasif_seg_sub') }}</div>
                </div>
                <span class="chart-badge chart-badge--blue">{{ $t('clasif_seg_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--tall"><canvas ref="chartClasifSegmentos"></canvas></div>
            </div>
          </div>

          <!-- Fila 3: KDE por cliente + KDE global -->
          <div class="charts-grid-2 mb-3">
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('clasif_kde_title') }}</div>
                  <div class="chart-subtitle">{{ $t('clasif_kde_sub') }}</div>
                </div>
                <span class="chart-badge chart-badge--green">{{ $t('clasif_kde_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--kde"><canvas ref="chartClasifKde"></canvas></div>
            </div>
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('clasif_kde_global_title') }}</div>
                  <div class="chart-subtitle">{{ $t('clasif_kde_global_sub') }}</div>
                </div>
                <span class="chart-badge">{{ $t('clasif_kde_global_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--kde"><canvas ref="chartClasifKdeGeneral"></canvas></div>
            </div>
          </div>

          <!-- Tabla: Desviaciones de confianza por categoría -->
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('clasif_desv_title') }}</div>
                <div class="chart-subtitle">{{ $t('clasif_desv_sub') }}</div>
              </div>
            </div>
            <div style="overflow-x:auto">
              <table class="ranking-table">
                <thead>
                  <tr>
                    <th>{{ $t('clasif_th_categoria') }}</th>
                    <th class="rt-col-num">{{ $t('clasif_th_conf') }}</th>
                    <th class="rt-col-num">{{ $t('clasif_th_articulos') }}</th>
                    <th class="rt-col-num">{{ $t('clasif_th_estado') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cat in categoriasConfianzaTabla" :key="cat.categoria">
                    <td class="rt-nombre">{{ cat.categoria }}</td>
                    <td :class="['rt-col-num', confianzaClass(cat.confianzaPromedio)]" style="padding:13px 12px;text-align:right;font-weight:700">
                      {{ cat.confianzaPromedio }}%
                    </td>
                    <td class="rt-match">{{ cat.totalArticulos.toLocaleString(numLocale()) }}</td>
                    <td style="padding:13px 12px;text-align:right">
                      <span :class="['clasif-estado', cat.confianzaPromedio >= 70 ? 'clasif-estado--ok' : cat.confianzaPromedio >= 50 ? 'clasif-estado--warn' : 'clasif-estado--danger']">
                        {{ cat.confianzaPromedio >= 70 ? $t('clasif_estado_normal') : cat.confianzaPromedio >= 50 ? $t('clasif_estado_revisar') : $t('clasif_estado_critico') }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!categoriasConfianzaTabla.length">
                    <td colspan="4" style="text-align:center;padding:24px;color:#9ca3af">{{ $t('clasif_sin_datos') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      <!-- PANEL: Monitoreo de Ventas -->
      <div v-show="activeTab === 'monitoreo'" class="dm-tab-panel">
        <div v-if="loadingVentasMonitoreo" class="dm-loading"><i class="el-icon-loading" /> {{ $t('dm_cargando') }}</div>
        <div v-else>

          <!-- Intro -->
          <div class="chart-card chart-card--featured mb-4 mon-intro-card">
            <div class="chart-title--lg">{{ $t('mon_intro_title') }}</div>
            <div class="chart-subtitle mt-1">{{ $t('mon_intro_sub') }}</div>
          </div>

          <!-- KPI Grid -->
          <div class="charts-grid-4 mb-4">
            <div class="mon-kpi-card">
              <div class="mon-kpi-card__top">
                <span class="mon-kpi-card__label">{{ $t('mon_kpi_facturacion_label') }}</span>
                <div class="mon-kpi-card__icon"><i class="fas fa-dollar-sign"></i></div>
              </div>
              <div class="mon-kpi-card__value">{{ ventasMonitoreo ? formatUSD(ventasMonitoreo.facuracionTotal) : '—' }}</div>
              <div class="mon-kpi-card__sub">{{ $t('mon_kpi_facturacion_sub') }}</div>
            </div>
            <div class="mon-kpi-card">
              <div class="mon-kpi-card__top">
                <span class="mon-kpi-card__label">{{ $t('mon_kpi_items_label') }}</span>
                <div class="mon-kpi-card__icon"><i class="fas fa-shopping-bag"></i></div>
              </div>
              <div class="mon-kpi-card__value">{{ ventasMonitoreo ? (ventasMonitoreo.itemsVendidos || 0).toLocaleString(numLocale()) : '—' }}</div>
              <div class="mon-kpi-card__sub">{{ $t('mon_kpi_items_sub') }}</div>
            </div>
            <div class="mon-kpi-card">
              <div class="mon-kpi-card__top">
                <span class="mon-kpi-card__label">{{ $t('mon_kpi_transacciones_label') }}</span>
                <div class="mon-kpi-card__icon"><i class="fas fa-check-circle"></i></div>
              </div>
              <div class="mon-kpi-card__value">{{ ventasMonitoreo ? (ventasMonitoreo.transaccionesCompletadas || 0).toLocaleString(numLocale()) : '—' }}</div>
              <div class="mon-kpi-card__sub">{{ $t('mon_kpi_transacciones_sub') }}</div>
            </div>
            <div class="mon-kpi-card">
              <div class="mon-kpi-card__top">
                <span class="mon-kpi-card__label">{{ $t('mon_kpi_ticket_label') }}</span>
                <div class="mon-kpi-card__icon"><i class="fas fa-chart-line"></i></div>
              </div>
              <div class="mon-kpi-card__value">{{ ventasMonitoreo ? formatUSDDecimal(ventasMonitoreo.ticketPromedio) : '—' }}</div>
              <div class="mon-kpi-card__sub">{{ $t('mon_kpi_ticket_sub') }}</div>
            </div>
          </div>

          <!-- Charts -->
          <div class="charts-grid-2 mb-4">
            <!-- Donut: Ventas por Categoría -->
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('mon_chart_cat_title') }}</div>
                  <div class="chart-subtitle">{{ $t('mon_chart_cat_sub') }}</div>
                </div>
                <span class="chart-badge chart-badge--blue">{{ $t('mon_chart_cat_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--donut"><canvas ref="chartMonitoreoCategoria"></canvas></div>
            </div>
            <!-- Line: Evolución temporal monetaria -->
            <div class="chart-card chart-card--featured">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('mon_chart_tl_title') }}</div>
                  <div class="chart-subtitle">{{ $t('mon_chart_tl_sub') }}</div>
                </div>
                <span class="chart-badge">{{ $t('mon_chart_tl_badge') }}</span>
              </div>
              <div class="chart-wrap chart-wrap--tall"><canvas ref="chartMonitoreoTimeline"></canvas></div>
            </div>
          </div>

          <!-- Detalle de transacciones -->
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('mon_det_title') }}</div>
                <div class="chart-subtitle">{{ $t('mon_det_sub') }}</div>
              </div>
              <span class="chart-badge">{{ $t('mon_det_badge') }}</span>
            </div>
            <div style="overflow-x:auto">
              <table class="ranking-table mon-det-table" style="min-width:900px">
                <thead>
                  <tr>
                    <th style="width:100px">{{ $t('mon_det_th_id') }}</th>
                    <th style="width:90px">{{ $t('mon_det_th_sku') }}</th>
                    <th style="width:110px">{{ $t('mon_det_th_fab') }}</th>
                    <th>{{ $t('mon_det_th_desc') }}</th>
                    <th style="width:150px">{{ $t('mon_det_th_cat') }}</th>
                    <th class="rt-col-num" style="width:70px">{{ $t('mon_det_th_cant') }}</th>
                    <th class="rt-col-num" style="width:120px">{{ $t('mon_det_th_precio') }}</th>
                    <th class="rt-col-num" style="width:110px">{{ $t('mon_det_th_monto') }}</th>
                    <th class="rt-col-num" style="width:120px">{{ $t('mon_det_th_fecha') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in ventasMonitoreo && ventasMonitoreo.detalles ? ventasMonitoreo.detalles : []"
                    :key="idx"
                  >
                    <td style="font-size:0.78rem;color:#3b82f6;padding:10px 12px">{{ row.idProducto || 'N/A' }}</td>
                    <td style="font-size:0.78rem;color:#6b7280;padding:10px 12px">{{ row.sku || '—' }}</td>
                    <td style="font-weight:700;font-size:0.82rem;padding:10px 12px">{{ row.fabricante || '—' }}</td>
                    <td style="font-size:0.82rem;padding:10px 12px" :title="row.descripcionRepuesto">{{ truncate(row.descripcionRepuesto, 30) }}</td>
                    <td style="font-size:0.78rem;color:#6b7280;padding:10px 12px">{{ row.categoriaInexlink }}</td>
                    <td class="rt-col-num" style="padding:10px 12px;font-weight:600">{{ row.cantidad }}</td>
                    <td class="rt-col-num" style="padding:10px 12px">
                      <span style="font-weight:700">${{ Number(row.precioVenta).toLocaleString(numLocale()) }}</span>
                      <span style="color:#9ca3af;font-size:0.72rem"> USD</span>
                    </td>
                    <td class="rt-col-num" style="padding:10px 12px">
                      <span style="font-weight:700">${{ Number(row.montoTotal).toLocaleString(numLocale()) }}</span>
                      <span style="color:#9ca3af;font-size:0.72rem"> USD</span>
                    </td>
                    <td class="rt-col-num" style="color:#9ca3af;font-size:0.78rem;padding:10px 12px">{{ row.fechaTransaccion }}</td>
                  </tr>
                  <tr v-if="!ventasMonitoreo || !ventasMonitoreo.detalles || !ventasMonitoreo.detalles.length">
                    <td colspan="9" style="text-align:center;padding:24px;color:#9ca3af">{{ $t('mon_det_sin_datos') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      <!-- PANEL: Fidelidad y RFM -->
      <div v-show="activeTab === 'rfm'" class="dm-tab-panel">
        <div v-if="loadingRfm" class="dm-loading"><i class="el-icon-loading" /> {{ $t('dm_cargando') }}</div>
        <div v-else>

          <!-- Intro -->
          <div class="chart-card chart-card--featured mb-4 rfm-intro-card">
            <div class="chart-title--lg mb-1">{{ $t('rfm_intro_title') }}</div>
            <div class="chart-subtitle mt-1">{{ $t('rfm_intro_sub') }}</div>
          </div>

          <!-- Fila 1: Scatter 3D (2/3) + Donut Churn (1/3) -->
          <div class="rfm-top-grid mb-4">

            <!-- 3D Scatter Plot -->
            <div class="chart-card chart-card--featured rfm-scatter-card">
              <div class="chart-header">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('rfm_3d_title') }}</div>
                  <div class="chart-subtitle">{{ $t('rfm_3d_sub') }}</div>
                </div>
                <span class="chart-badge chart-badge--blue">3D Scatter</span>
              </div>
              <div class="rfm-3d-wrap">
                <canvas
                  ref="rfm3dCanvas"
                  class="rfm-3d-canvas"
                  width="550"
                  height="250"
                  @mousedown="rfm3dMouseDown"
                  @mousemove="rfm3dMouseMove"
                  @mouseup="rfm3dMouseUp"
                  @mouseleave="rfm3dMouseUp"
                  @wheel.prevent="rfm3dWheel"
                ></canvas>
              </div>
              <div class="rfm-zoom-row">
                <div class="rfm-zoom-label">
                  <i class="fas fa-search-plus mr-1"></i>
                  <span>{{ $t('rfm_zoom') }}</span>
                </div>
                <input
                  type="range"
                  min="50" max="250"
                  :value="rfm3dZoomPct"
                  class="rfm-zoom-slider"
                  @input="rfm3dZoomChange($event.target.value)"
                />
                <span class="rfm-zoom-val">{{ rfm3dZoomPct }}%</span>
              </div>
            </div>

            <!-- Donut Churn + Criterios -->
            <div class="chart-card chart-card--featured rfm-donut-card">
              <div class="chart-header mb-2">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('rfm_churn_title') }}</div>
                  <div class="chart-subtitle">{{ $t('rfm_churn_sub') }}</div>
                </div>
              </div>
              <div class="rfm-donut-wrap">
                <canvas ref="chartRfmDonut"></canvas>
              </div>
              <div class="rfm-criteria mt-3">
                <div class="rfm-criteria-row">
                  <span class="rfm-criteria-label rfm-criteria-label--activo">{{ $t('rfm_seg_activo_label') }}:</span>
                  <span class="rfm-criteria-val">{{ $t('rfm_criterio_activo') }}</span>
                </div>
                <div class="rfm-criteria-row">
                  <span class="rfm-criteria-label rfm-criteria-label--riesgo">{{ $t('rfm_seg_riesgo_label') }}:</span>
                  <span class="rfm-criteria-val">{{ $t('rfm_criterio_riesgo') }}</span>
                </div>
                <div class="rfm-criteria-row">
                  <span class="rfm-criteria-label rfm-criteria-label--inactivo">{{ $t('rfm_seg_inactivo_label') }} / {{ $t('rfm_fugado') }}:</span>
                  <span class="rfm-criteria-val">{{ $t('rfm_criterio_inactivo') }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Fila 2: 3 charts horizontales (Recency | Frequency | Monetary) -->
          <div class="charts-grid-3 mb-4">
            <div class="chart-card chart-card--featured">
              <div class="chart-header mb-2">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('rfm_chart_rec_title') }}</div>
                  <div class="chart-subtitle">{{ $t('rfm_chart_rec_sub') }}</div>
                </div>
              </div>
              <div class="chart-wrap" style="height:220px"><canvas ref="chartRfmRecency"></canvas></div>
            </div>
            <div class="chart-card chart-card--featured">
              <div class="chart-header mb-2">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('rfm_chart_freq_title') }}</div>
                  <div class="chart-subtitle">{{ $t('rfm_chart_freq_sub2') }}</div>
                </div>
              </div>
              <div class="chart-wrap" style="height:220px"><canvas ref="chartRfmFrequency"></canvas></div>
            </div>
            <div class="chart-card chart-card--featured">
              <div class="chart-header mb-2">
                <div>
                  <div class="chart-title chart-title--lg">{{ $t('rfm_chart_monetary_title') }}</div>
                  <div class="chart-subtitle">{{ $t('rfm_chart_monetary_sub') }}</div>
                </div>
              </div>
              <div class="chart-wrap" style="height:220px"><canvas ref="chartRfmMonetary"></canvas></div>
            </div>
          </div>

          <!-- Tabla con 8 columnas -->
          <div class="chart-card chart-card--featured">
            <div class="chart-header">
              <div>
                <div class="chart-title chart-title--lg">{{ $t('rfm_tabla_title') }}</div>
                <div class="chart-subtitle">{{ $t('rfm_tabla_sub') }}</div>
              </div>
              <span class="chart-badge">{{ $t('rfm_tabla_badge') }}</span>
            </div>
            <div style="overflow-x:auto">
              <table class="ranking-table" style="min-width:960px">
                <thead>
                  <tr>
                    <th>{{ $t('rfm_th_cliente') }}</th>
                    <th class="rt-col-num">{{ $t('rfm_th_recencia') }}</th>
                    <th class="rt-col-num">{{ $t('rfm_th_freq1m') }}</th>
                    <th class="rt-col-num">{{ $t('rfm_th_freq3m') }}</th>
                    <th class="rt-col-num">{{ $t('rfm_th_frecuencia') }}</th>
                    <th class="rt-col-num">{{ $t('rfm_th_monetary') }}</th>
                    <th class="rt-col-num">{{ $t('rfm_th_estado') }}</th>
                    <th class="rt-col-num">{{ $t('rfm_th_ultimo_lote') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in rfmData" :key="'tbl-' + r.nombreCliente">
                    <td class="rt-nombre">{{ r.nombreCliente }}</td>
                    <td class="rt-col-num" style="padding:13px 12px;font-family:monospace">
                      <span :style="{
                        fontWeight: 700,
                        color: r.recency > 45 ? '#ef4444' : r.recency > 15 ? '#f59e0b' : '#10b981'
                      }">
                        {{ r.recency === 9999 ? $t('rfm_sin_registros') : r.recency === 0 ? $t('rfm_hoy') : r.recency + ' ' + $t('rfm_dias') }}
                      </span>
                    </td>
                    <td class="rt-col-num" style="padding:13px 12px;font-family:monospace">
                      {{ r.frequency1M != null ? r.frequency1M : r.frequency }} {{ $t('rfm_archivos') }}
                    </td>
                    <td class="rt-col-num" style="padding:13px 12px;font-family:monospace">
                      {{ r.frequency3M != null ? r.frequency3M : r.frequency }} {{ $t('rfm_archivos') }}
                    </td>
                    <td class="rt-col-num" style="padding:13px 12px;font-family:monospace;font-weight:600">
                      {{ r.frequency }} {{ $t('rfm_archivos') }}
                    </td>
                    <td class="rt-col-num" style="padding:13px 12px;font-family:monospace;font-weight:700;color:#059669">
                      {{ r.monetary != null ? formatUSD(r.monetary) : '—' }}
                    </td>
                    <td class="rt-col-num" style="padding:13px 12px">
                      <span :class="[
                        'rfm-estado-badge',
                        r.estado === 'Activo' ? 'rfm-estado-badge--activo' :
                        r.estado === 'En Riesgo' ? 'rfm-estado-badge--riesgo' : 'rfm-estado-badge--inactivo'
                      ]">{{ r.estado }}</span>
                    </td>
                    <td class="rt-col-num" style="padding:13px 12px;font-size:0.73rem;color:#9ca3af;font-family:monospace">
                      {{ r.ultimoLote || r.lastUploadDate || '—' }}
                    </td>
                  </tr>
                  <tr v-if="!rfmData.length">
                    <td colspan="8" style="text-align:center;padding:24px;color:#9ca3af">{{ $t('rfm_sin_datos') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import dashboardMasterService from '@/api/dashboardMasterService';
import utilsService from '@/api/utilsService';
import materialService from '@/api/materialService';
import Swal from 'sweetalert2';

let Chart;
try { Chart = require('chart.js'); } catch (e) { /* fallback */ }

const COLORS = [
  '#00A3E0', '#36A2EB', '#4BC0C0', '#FF6384', '#FF9F40',
  '#9966FF', '#FFCD56', '#C9CBCF', '#00C49F', '#E91E63',
];

export default {
  name: 'DashboardMaster',

  data() {
    return {
      activeTab: 'resumen',
      idEmpresaSeleccionada: 0,
      isAdmin: false,
      empresaCliente: null,

      // Estado de la tabla de reporteria que alimenta el dashboard.
      consolidando: false,
      consolidacionEstado: null,

      // loading flags
      loadingKpis: false,
      loadingClientes: false,
      loadingCategorias: false,
      loadingTimeline: false,
      loadingRfm: false,
      loadingAlertas: false,
      loadingTable: false,
      loadingClases: false,
      loadingMarcas: false,
      loadingDesgloce: false,
      loadingFamilias: false,
      loadingSegmentos: false,
      loadingConfianza: false,
      loadingVentasMonitoreo: false,

      // data
      kpis: null,
      clientesResumen: [],
      categorias: [],
      timeline: [],
      ventasTimeline: [],
      rfmData: [],
      rfm3dRotationX: 25,
      rfm3dRotationY: -45,
      rfm3dScale: 280,
      rfm3dPoints: [],
      rfm3dDragging: false,
      rfm3dPrevX: 0,
      rfm3dPrevY: 0,
      rfm3dZoomPct: 200,
      alertas: [],
      clases: [],
      marcas: [],
      desgloce: null,
      familias: [],
      segmentos: [],
      confianzaDist: null,
      ventasMonitoreo: null,

      // table
      tablePaged: { items: [], total: 0, page: 1, pageSize: 50, totalPages: 0 },
      tablePage: 1,
      tablePageSize: 50,
      tableSearch: '',
      searchTimeout: null,

      // chart instances (para destroy antes de re-render)
      charts: {},

      lang: localStorage.getItem('language') || 'es',
    };
  },

  computed: {
    // Consolidar reconstruye la tabla de reportería: queda para los perfiles de
    // gestión, igual que la política "EmpresaManagement" del endpoint
    // POST /api/dashboardmaster/consolidar.
    puedeConsolidar() {
      return this.puedeEscribir
        && ['admin', 'ventas inex', 'operaciones'].includes(utilsService.getRol());
    },

    loadingAny() {
      return this.loadingKpis || this.loadingClientes || this.loadingCategorias ||
        this.loadingTimeline || this.loadingRfm || this.loadingAlertas || this.loadingTable;
    },

    nombreEmpresaCliente() {
      return this.empresaCliente ? this.empresaCliente.nombre : '—';
    },

    clientesOrdenados() {
      return [...this.clientesResumen].sort((a, b) => b.confianzaPromedio - a.confianzaPromedio);
    },

    timelineTokens() {
      // Un registro por (token + fecha), ordenado por fecha desc
      const seen = new Set();
      const result = [];
      for (const t of [...this.timeline].sort((a, b) => b.fecha.localeCompare(a.fecha))) {
        const key = `${t.token}__${t.fecha}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push(t);
        }
      }
      return result;
    },

    kpiCardsPrimarios() {
      void this.lang;
      if (!this.kpis) return [];
      const k = this.kpis;
      const pct = k.stockTotalValorizado > 0
        ? Math.abs(Math.round(k.ajustePreciosSugerido / k.stockTotalValorizado * 100 * 10) / 10)
        : 0;
      return [
        {
          key: 'stock',
          label: this.$t('dm_kpi_stock_label'),
          value: this.formatUSD(k.stockTotalValorizado),
          sub: this.$t('dm_kpi_stock_sub', { value: this.formatUSD(k.ajustePreciosSugerido), pct }),
          subClass: k.ajustePreciosSugerido < 0 ? 'kpi-p-sub--green' : 'kpi-p-sub--red',
          sectionLabel: this.$t('dm_kpi_stock_section'),
          description: this.$t('dm_kpi_stock_desc'),
        },
        {
          key: 'ventas',
          label: this.$t('dm_kpi_ventas_label'),
          value: this.formatUSD(k.montoTransaccionesVentas),
          sub: this.$t('dm_kpi_ventas_sub'),
          subClass: 'kpi-p-sub--muted',
          sectionLabel: this.$t('dm_kpi_ventas_section'),
          description: this.$t('dm_kpi_ventas_desc'),
        },
        {
          key: 'eficiencia',
          label: this.$t('dm_kpi_eficiencia_label'),
          value: `${k.tasaMatch}%`,
          icon: 'fas fa-check-double',
          sub: this.$t('dm_kpi_eficiencia_sub', { homologados: (k.itemsHomologados || 0).toLocaleString(this.numLocale()), totales: (k.itemsTotales || 0).toLocaleString(this.numLocale()) }),
          subClass: 'kpi-p-sub--green',
          sectionLabel: this.$t('dm_kpi_eficiencia_section'),
          description: this.$t('dm_kpi_eficiencia_desc'),
        },
        {
          key: 'archivos',
          label: this.$t('dm_kpi_archivos_label'),
          value: k.archivosProcessados,
          icon: 'fas fa-file-alt',
          sub: this.$t('dm_kpi_archivos_sub', { promedio: k.promedioItemsPorArchivo }),
          subClass: 'kpi-p-sub--muted',
          sectionLabel: this.$t('dm_kpi_archivos_section'),
          description: this.$t('dm_kpi_archivos_desc'),
        },
      ];
    },

    kpiCardsSecundarios() {
      void this.lang;
      if (!this.kpis) return [];
      const k = this.kpis;
      const diasLabel = k.ultimaIngestionDias === 0
        ? this.$t('dm_kpi_ingesta_hoy')
        : k.ultimaIngestionDias > 0
          ? this.$t('dm_kpi_ingesta_dias', { n: k.ultimaIngestionDias })
          : '—';
      const pctPub = k.itemsTotales > 0
        ? Math.round(k.itemsPublicados / k.itemsTotales * 1000) / 10
        : 0;
      return [
        {
          key: 'items',
          label: this.$t('dm_kpi_items_label'),
          value: (k.itemsTotales || 0).toLocaleString(this.numLocale()),
          sub: this.$t('dm_kpi_items_sub', { publicados: (k.itemsPublicados || 0).toLocaleString(this.numLocale()), pct: pctPub }),
          icon: 'fas fa-boxes',
          iconBg: '#EFF6FF', iconColor: '#3B82F6',
        },
        {
          key: 'ingesta',
          label: this.$t('dm_kpi_ingesta_label'),
          value: diasLabel,
          sub: k.ultimaIngestionFecha
            ? this.$t('dm_kpi_ingesta_fecha', { fecha: new Date(k.ultimaIngestionFecha).toLocaleDateString(this.numLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }) })
            : this.$t('dm_kpi_ingesta_sin_fecha'),
          icon: 'fas fa-clock',
          iconBg: k.ultimaIngestionDias > 15 ? '#FFF7ED' : '#ECFDF5',
          iconColor: k.ultimaIngestionDias > 15 ? '#F97316' : '#10B981',
        },
        {
          key: 'vendidos',
          label: this.$t('dm_kpi_vendidos_label'),
          value: (k.itemsVendidos || 0).toLocaleString(this.numLocale()),
          sub: this.$t('dm_kpi_vendidos_sub'),
          icon: 'fas fa-shopping-bag',
          iconBg: '#F0FDF4', iconColor: '#22C55E',
        },
        {
          key: 'transacciones',
          label: this.$t('dm_kpi_transacciones_label'),
          value: (k.transaccionesCompletadas || 0).toLocaleString(this.numLocale()),
          sub: this.$t('dm_kpi_transacciones_sub'),
          icon: 'fas fa-check-circle',
          iconBg: '#FFF7ED', iconColor: '#F59E0B',
        },
      ];
    },

    categoriasTop() {
      return this.categorias.slice(0, 15);
    },

    timelineArchivos() {
      return [...this.timeline].sort((a, b) => b.fecha.localeCompare(a.fecha)).slice(0, 50);
    },

    alertasCriticas() {
      return this.alertas.filter(a => a.tipo === 'critical').length;
    },

    alertasWarning() {
      return this.alertas.filter(a => a.tipo === 'warning').length;
    },

    rfmActivos() {
      return this.rfmData.filter(r => r.estado === 'Activo').length;
    },

    rfmRiesgo() {
      return this.rfmData.filter(r => r.estado === 'En Riesgo').length;
    },

    rfmInactivos() {
      return this.rfmData.filter(r => r.estado !== 'Activo' && r.estado !== 'En Riesgo').length;
    },

    categoriasConfianzaTabla() {
      return this.categorias
        .filter(c => c.totalArticulos >= 5 && c.confianzaPromedio > 0)
        .slice()
        .sort((a, b) => a.confianzaPromedio - b.confianzaPromedio)
        .slice(0, 10);
    },
  },

  watch: {
    activeTab(tab) {
      this.$nextTick(() => this.renderChartsForTab(tab));
    },
    rfmData(newVal) {
      // Si el tab RFM ya está visible cuando llegan los datos, renderizar
      if (this.activeTab === 'rfm' && Array.isArray(newVal) && newVal.length) {
        this.$nextTick(() => this.renderRfmCharts());
      }
    },
    idEmpresaSeleccionada(val) {
      if (val > 0 && this.activeTab === 'clasificacion') {
        this.activeTab = 'resumen';
      }
      this.$nextTick(() => this.renderChartsForTab(this.activeTab));
    },
  },

  created() {
    this.detectRole();
  },

  async mounted() {
    this.$store.dispatch('setSidebarCollapsed', false);
    const self = this;
    const origSet = this.$setLanguage ? this.$setLanguage.bind(this) : null;
    this.$setLanguage = function(l) {
      if (origSet) origSet(l);
      self.lang = l;
    };
    if (!this.isAdmin) {
      await this.loadEmpresaCliente();
    }
    this.loadConsolidacionEstado();
    await this.loadAll();
  },

  beforeDestroy() {
    this.$store.dispatch('setSidebarCollapsed', false);
    Object.values(this.charts).forEach(c => c && c.destroy());
  },

  methods: {
    detectRole() {
      const rol = utilsService.getRol();
      this.isAdmin = this.esSoloLectura || ['admin', 'ventas inex', 'operaciones'].includes(rol);
    },

    formatoFechaHora(valor) {
      if (!valor) return '';
      const d = new Date(valor);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('es-CL', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },

    async loadConsolidacionEstado() {
      try {
        this.consolidacionEstado = await dashboardMasterService.getConsolidacionEstado();
      } catch (e) {
        // El indicador de "datos al" es informativo, no debe romper el dashboard.
        this.consolidacionEstado = null;
      }
    },

    async consolidarDatos() {
      if (!this.puedeConsolidar) return;
      const confirmacion = await Swal.fire({
        title: this.$t('dm_consolidar'),
        text: this.$t('dm_consolidar_confirmar'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('dm_consolidar'),
        cancelButtonText: this.$t('cancelar'),
      });
      if (!confirmacion.isConfirmed) return;

      this.consolidando = true;
      Swal.fire({
        html: '<div style="display:flex;align-items:center;justify-content:center;">' +
              '<img src="/img/others/loading.gif" alt="Loading" style="width:50px;height:50px;margin-right:15px;">' +
              '<div><h3 style="color:#006393;">' + this.$t('dm_consolidando') + '</h3></div>' +
              '</div>',
        showConfirmButton: false,
        allowOutsideClick: false,
      });

      try {
        const r = await dashboardMasterService.consolidar();
        await this.loadConsolidacionEstado();
        await this.loadAll();

        const filas = (r && r.filasProcesadas != null) ? r.filasProcesadas : 0;
        const seg = (r && r.duracionMs != null) ? Math.round(r.duracionMs / 100) / 10 : 0;
        Swal.fire({
          icon: 'success',
          title: this.$t('dm_consolidado_ok'),
          text: `${filas} ${this.$t('dm_filas_en')} ${seg}s`,
          timer: 3000,
          showConfirmButton: false,
        });
      } catch (e) {
        const data = (e && e.response) ? e.response.data : null;
        const msg = (data && data.mensaje) ? data.mensaje
                  : (data && data.message) ? data.message
                  : (e && e.message) ? e.message
                  : this.$t('dm_consolidado_error');
        Swal.fire({ icon: 'error', title: 'Oops...', text: msg });
      } finally {
        this.consolidando = false;
      }
    },

    async loadEmpresaCliente() {
      try {
        const resp = await materialService.getEmpresasAsociadas();
        const raw = Array.isArray(resp) ? resp
          : (resp && Array.isArray(resp.data)) ? resp.data
          : (resp && Array.isArray(resp.items)) ? resp.items
          : [];
        const empresa = raw
          .map(e => ({
            id: e.id != null ? e.id : (e.empresaId != null ? e.empresaId : e.Id),
            nombre: e.nombre || e.razonSocial || e.Nombre || '',
          }))
          .find(e => e.id && e.nombre);
        if (empresa) {
          this.empresaCliente = empresa;
          this.idEmpresaSeleccionada = empresa.id;
        }
      } catch (e) { /* sin empresa asignada */ }
    },

    async loadAll() {
      const idE = this.idEmpresaSeleccionada || 0;
      const promises = [
        this.loadKpis(idE),
        this.loadCategorias(idE),
        this.loadAlertas(idE),
        this.loadTable(idE, 1, this.tablePageSize),
        this.loadClases(idE),
        this.loadMarcas(idE),
        this.loadClientes(),
        this.loadTimeline(idE),
        this.loadRfm(),
        this.loadVentasTimeline(idE),
        this.loadVentasMonitoreo(idE),
      ];
      if (idE > 0) promises.push(this.loadDesgloce(idE));
      else this.desgloce = null;
      if (idE === 0) {
        promises.push(this.loadFamilias(0));
        promises.push(this.loadSegmentos(0));
        promises.push(this.loadConfianzaDistribucion(0));
      } else {
        this.familias = [];
        this.segmentos = [];
        this.confianzaDist = null;
      }
      await Promise.all(promises);
      this.$nextTick(() => this.renderChartsForTab(this.activeTab));
    },

    onEmpresaChange() {
      this.tablePage = 1;
      this.loadAll();
    },

    async loadKpis(idEmpresa = 0) {
      this.loadingKpis = true;
      try { this.kpis = await dashboardMasterService.getKpis(idEmpresa); }
      catch (e) { this.$message.error('Error al cargar KPIs'); }
      finally { this.loadingKpis = false; }
    },

    async loadClientes() {
      this.loadingClientes = true;
      try { this.clientesResumen = await dashboardMasterService.getClientes(); }
      catch (e) { /* 403 esperado para no-admins */ }
      finally { this.loadingClientes = false; }
    },

    async loadCategorias(idEmpresa = 0) {
      this.loadingCategorias = true;
      try { this.categorias = await dashboardMasterService.getCategorias(idEmpresa, this.lang); }
      catch (e) { this.$message.error('Error al cargar categorías'); }
      finally { this.loadingCategorias = false; }
    },

    async loadTimeline(idEmpresa = 0) {
      this.loadingTimeline = true;
      try { this.timeline = await dashboardMasterService.getTimeline(idEmpresa); }
      catch (e) { this.$message.error('Error al cargar cronología'); }
      finally { this.loadingTimeline = false; }
    },

    async loadRfm() {
      this.loadingRfm = true;
      try {
        const res = await dashboardMasterService.getRfm();
        // Normalizar respuesta: puede venir como array, { data:[...] } o { $values:[...] }
        this.rfmData = Array.isArray(res) ? res
          : Array.isArray(res && res.data) ? res.data
          : Array.isArray(res && res.items) ? res.items
          : Array.isArray(res && res.$values) ? res.$values
          : [];
      } catch (e) { /* 403 esperado para roles sin acceso */ }
      finally {
        this.loadingRfm = false;
        // Re-render si ya estamos en el tab RFM cuando llegan los datos
        if (this.activeTab === 'rfm') {
          this.$nextTick(() => this.renderRfmCharts());
        }
      }
    },

    async loadAlertas(idEmpresa = 0) {
      this.loadingAlertas = true;
      try { this.alertas = await dashboardMasterService.getAlertas(idEmpresa); }
      catch (e) { this.$message.error('Error al cargar alertas'); }
      finally { this.loadingAlertas = false; }
    },

    async loadClases(idEmpresa = 0) {
      this.loadingClases = true;
      try { this.clases = await dashboardMasterService.getClases(idEmpresa, this.lang); }
      catch (e) { this.$message.error('Error al cargar clases'); }
      finally { this.loadingClases = false; }
    },

    async loadMarcas(idEmpresa = 0) {
      this.loadingMarcas = true;
      try { this.marcas = await dashboardMasterService.getMarcas(idEmpresa, 10, this.lang); }
      catch (e) { this.$message.error('Error al cargar marcas'); }
      finally { this.loadingMarcas = false; }
    },

    async loadVentasTimeline(idEmpresa = 0) {
      try { this.ventasTimeline = await dashboardMasterService.getVentasTimeline(idEmpresa); }
      catch (e) { /* no crítico */ }
    },

    async loadVentasMonitoreo(idEmpresa = 0) {
      this.loadingVentasMonitoreo = true;
      try { this.ventasMonitoreo = await dashboardMasterService.getVentasMonitoreo(idEmpresa); }
      catch (e) { /* no crítico */ }
      finally { this.loadingVentasMonitoreo = false; }
    },

    async loadDesgloce(idEmpresa) {
      this.loadingDesgloce = true;
      try { this.desgloce = await dashboardMasterService.getDesgloce(idEmpresa); }
      catch (e) { this.$message.error('Error al cargar desglose'); }
      finally { this.loadingDesgloce = false; }
    },

    async loadFamilias(idEmpresa = 0) {
      this.loadingFamilias = true;
      try { this.familias = await dashboardMasterService.getFamilias(idEmpresa, this.lang); }
      catch (e) { /* no crítico */ }
      finally { this.loadingFamilias = false; }
    },

    async loadSegmentos(idEmpresa = 0) {
      this.loadingSegmentos = true;
      try { this.segmentos = await dashboardMasterService.getSegmentos(idEmpresa, this.lang); }
      catch (e) { /* no crítico */ }
      finally { this.loadingSegmentos = false; }
    },

    async loadConfianzaDistribucion(idEmpresa = 0) {
      this.loadingConfianza = true;
      try { this.confianzaDist = await dashboardMasterService.getConfianzaDistribucion(idEmpresa); }
      catch (e) { /* no crítico */ }
      finally { this.loadingConfianza = false; }
    },

    async loadTable(idEmpresa = 0, page = 1, pageSize = 50, search = '') {
      this.loadingTable = true;
      try {
        this.tablePaged = await dashboardMasterService.getMateriales({
          idEmpresa, page, pageSize, search,
        });
      } catch (e) { this.$message.error('Error al cargar tabla'); }
      finally { this.loadingTable = false; }
    },

    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.tablePage = 1;
        this.loadTable(
          this.idEmpresaSeleccionada,
          1,
          this.tablePageSize,
          this.tableSearch,
        );
      }, 400);
    },

    onPageChange(page) {
      this.tablePage = page;
      this.loadTable(this.idEmpresaSeleccionada, page, this.tablePageSize, this.tableSearch);
    },

    onPageSizeChange(size) {
      this.tablePageSize = size;
      this.tablePage = 1;
      this.loadTable(this.idEmpresaSeleccionada, 1, size, this.tableSearch);
    },

    // ── Charts ────────────────────────────────────────────────────────────

    renderChartsForTab(tab) {
      if (!Chart) return;
      if (tab === 'resumen') {
        if (this.idEmpresaSeleccionada > 0) this.renderClienteCharts();
        else this.renderResumenCharts();
      }
      if (tab === 'cronologia') this.renderTimelineChart();
      if (tab === 'rfm') this.renderRfmCharts();
      if (tab === 'auditoria') this.renderAlertasChart();
      if (tab === 'clasificacion') this.renderClasificacionCharts();
      if (tab === 'monitoreo') this.renderMonitoreoCharts();
    },

    destroyChart(key) {
      if (this.charts[key]) { this.charts[key].destroy(); delete this.charts[key]; }
    },

    _saludPct(t) {
      if (!t.cantidad) return 0;
      return Math.round(((t.cantidad - t.criticos) / t.cantidad) * 100);
    },

    _saludColor(t) {
      const pct = this._saludPct(t);
      if (pct >= 90) return '#10b981';
      if (pct >= 60) return '#f59e0b';
      return '#ef4444';
    },

    filtrarPorToken(token) {
      // TODO: filtrar el panel por token cuando se implemente el filtro de lote
      this.$message.info(`Filtro por lote: ${token.substring(0, 18)}...`);
    },

    renderResumenCharts() {
      if (!this.isAdmin || !this.clientesResumen.length) return;
      const labels = this.clientesResumen.map(c => c.nombreCliente);

      // Clientes: mixed bar (items) + line (valor stock) con doble eje Y
      this.destroyChart('clientes');
      const ctxC = this.$refs.chartClientes;
      if (ctxC) {
        const formatStock = v => {
          if (v === 0) return '$0';
          if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
          if (Math.abs(v) >= 1e6) return `$${Math.round(v / 1e6).toLocaleString(this.numLocale())} M`;
          return `$${Math.round(v).toLocaleString(this.numLocale())}`;
        };
        this.charts.clientes = new Chart(ctxC, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: this.$t('dm_chart_label_items_subidos'),
                type: 'bar',
                data: this.clientesResumen.map(c => c.totalItems),
                backgroundColor: 'rgba(91,141,239,0.85)',
                borderColor: 'rgba(91,141,239,1)',
                borderWidth: 1,
                yAxisID: 'y-items',
                order: 2,
              },
              {
                label: this.$t('dm_chart_label_valor_stock'),
                type: 'line',
                data: this.clientesResumen.map(c => c.valorTotal),
                borderColor: '#10B981',
                backgroundColor: 'transparent',
                borderWidth: 2.5,
                pointBackgroundColor: '#10B981',
                pointRadius: 5,
                pointHoverRadius: 7,
                fill: false,
                tension: 0,
                yAxisID: 'y-stock',
                order: 1,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
              position: 'top',
              align: 'center',
              labels: { boxWidth: 32, padding: 20, fontSize: 12 },
            },
            scales: {
              xAxes: [{
                gridLines: { display: false },
                ticks: { fontSize: 12, fontColor: '#6b7280' },
              }],
              yAxes: [
                {
                  id: 'y-items',
                  position: 'left',
                  ticks: {
                    beginAtZero: true,
                    fontSize: 11,
                    fontColor: '#6b7280',
                    callback: v => v.toLocaleString(this.numLocale()),
                  },
                  gridLines: { color: '#f3f4f6' },
                },
                {
                  id: 'y-stock',
                  position: 'right',
                  ticks: {
                    beginAtZero: true,
                    fontSize: 11,
                    fontColor: '#10B981',
                    callback: v => formatStock(v),
                  },
                  gridLines: { drawOnChartArea: false },
                },
              ],
            },
            tooltips: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (item, data) => {
                  const ds = data.datasets[item.datasetIndex];
                  const val = item.yLabel;
                  if (ds.yAxisID === 'y-stock') {
                    return ` ${ds.label}: ${formatStock(val)}`;
                  }
                  return ` ${ds.label}: ${val.toLocaleString(this.numLocale())}`;
                },
              },
            },
          },
        });
      }

      // Doughnut financiero: valor por categoría
      this.destroyChart('donutValor');
      const ctxDV = this.$refs.chartDonutValor;
      if (ctxDV && this.categorias.length) {
        const top7 = this.categorias.slice(0, 7);
        const DONUT_COLORS = ['#5B8DEF','#10B981','#F97316','#EC4899','#8B5CF6','#FBBF24','#06B6D4'];
        this.charts.donutValor = new Chart(ctxDV, {
          type: 'doughnut',
          data: {
            labels: top7.map(c => c.categoria.toUpperCase()),
            datasets: [{
              data: top7.map(c => c.valorTotal),
              backgroundColor: DONUT_COLORS,
              borderWidth: 2,
              borderColor: '#fff',
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: { fontSize: 10, padding: 10, boxWidth: 12 },
            },
            tooltips: {
              callbacks: {
                label: (item, data) => {
                  const val = data.datasets[0].data[item.index];
                  const label = data.labels[item.index];
                  return ` ${label}: $${Math.round(val).toLocaleString(this.numLocale())}`;
                },
              },
            },
          },
        });
      }

      // Doughnut cantidad: items por categoría
      this.destroyChart('donutCantidad');
      const ctxDC = this.$refs.chartDonutCantidad;
      if (ctxDC && this.categorias.length) {
        const top7c = this.categorias
          .slice()
          .sort((a, b) => b.totalArticulos - a.totalArticulos)
          .slice(0, 7);
        const DONUT_COLORS2 = ['#8B5CF6','#5B8DEF','#10B981','#F97316','#FBBF24','#EC4899','#06B6D4'];
        this.charts.donutCantidad = new Chart(ctxDC, {
          type: 'doughnut',
          data: {
            labels: top7c.map(c => c.categoria.toUpperCase()),
            datasets: [{
              data: top7c.map(c => c.totalArticulos),
              backgroundColor: DONUT_COLORS2,
              borderWidth: 2,
              borderColor: '#fff',
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: { fontSize: 10, padding: 10, boxWidth: 12 },
            },
            tooltips: {
              callbacks: {
                label: (item, data) => {
                  const val = data.datasets[0].data[item.index];
                  return ` ${data.labels[item.index]}: ${Number(val).toLocaleString(this.numLocale())} ${this.$t('dm_tooltip_unidades')}`;
                },
              },
            },
          },
        });
      }

      // Horizontal bar: Match Rate por cliente
      this.destroyChart('matchRate');
      const ctxM = this.$refs.chartMatchRate;
      if (ctxM) {
        const sorted = [...this.clientesResumen].sort((a, b) => b.tasaMatch - a.tasaMatch);
        this.charts.matchRate = new Chart(ctxM, {
          type: 'horizontalBar',
          data: {
            labels: sorted.map(c => c.nombreCliente),
            datasets: [{
              label: this.$t('dm_chart_label_match_rate'),
              data: sorted.map(c => c.tasaMatch),
              backgroundColor: 'rgba(52,211,153,0.85)',
              borderColor: 'rgba(16,185,129,1)',
              borderWidth: 1,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            scales: {
              xAxes: [{
                ticks: { min: 0, max: 100, fontSize: 11, fontColor: '#6b7280' },
                gridLines: { color: '#f3f4f6' },
              }],
              yAxes: [{
                ticks: { fontSize: 11, fontColor: '#374151' },
                gridLines: { display: false },
              }],
            },
            tooltips: {
              callbacks: { label: item => ` ${this.$t('dm_tooltip_match_rate')} ${item.xLabel}%` },
            },
          },
        });
      }

      // Doughnut por clase (fila 3)
      this.destroyChart('clase');
      const ctxCl = this.$refs.chartClase;
      if (ctxCl && this.clases.length) {
        const CLASE_COLORS = ['#F97316','#5B8DEF','#10B981','#EC4899','#8B5CF6','#FBBF24'];
        const claseRegistrosLabel = this.$t('dm_tooltip_registros');
        const locale = this.numLocale();
        this.charts.clase = new Chart(ctxCl, {
          type: 'doughnut',
          data: {
            labels: this.clases.map(c => c.clase.toUpperCase()),
            datasets: [{
              data: this.clases.map(c => c.totalArticulos),
              backgroundColor: CLASE_COLORS,
              borderWidth: 2,
              borderColor: '#fff',
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: {
                fontSize: 10, padding: 12, boxWidth: 12,
                generateLabels: function(chart) {
                  return chart.data.labels.map((label, i) => ({
                    text: `${label}: ${Number(chart.data.datasets[0].data[i]).toLocaleString(locale)} ${claseRegistrosLabel}`,
                    fillStyle: CLASE_COLORS[i % CLASE_COLORS.length],
                    strokeStyle: '#fff',
                    lineWidth: 2,
                    hidden: false,
                    index: i,
                  }));
                },
              },
            },
            tooltips: {
              callbacks: {
                label: (item, data) => {
                  const val = data.datasets[0].data[item.index];
                  return ` ${data.labels[item.index]}: ${Number(val).toLocaleString(this.numLocale())} ${this.$t('dm_tooltip_registros')}`;
                },
              },
            },
          },
        });
      }

      // Barras verticales: confianza IA por cliente (fila 3)
      this.destroyChart('confianzaCliente');
      const ctxCf = this.$refs.chartConfianzaCliente;
      if (ctxCf) {
        const withConf = this.clientesResumen
          .filter(c => c.confianzaPromedio > 0)
          .sort((a, b) => b.confianzaPromedio - a.confianzaPromedio);
        this.charts.confianzaCliente = new Chart(ctxCf, {
          type: 'bar',
          data: {
            labels: withConf.map(c => c.nombreCliente),
            datasets: [{
              label: this.$t('dm_chart_label_confianza_ia'),
              data: withConf.map(c => c.confianzaPromedio),
              backgroundColor: 'rgba(91,141,239,0.85)',
              borderColor: 'rgba(91,141,239,1)',
              borderWidth: 1,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            scales: {
              xAxes: [{
                gridLines: { display: false },
                ticks: { fontSize: 11, fontColor: '#374151' },
              }],
              yAxes: [{
                ticks: { min: 0, max: 100, fontSize: 11, fontColor: '#6b7280',
                  stepSize: 10 },
                gridLines: { color: '#f3f4f6' },
              }],
            },
            tooltips: {
              callbacks: { label: item => ` ${this.$t('dm_tooltip_confianza')} ${item.yLabel}%` },
            },
          },
        });
      }

      // ── Evolución temporal ventas ────────────────────────────────────────
      this.destroyChart('ventasTimeline');
      const ctxVT = this.$refs.chartVentasTimeline;
      if (ctxVT && this.ventasTimeline.length) {
        const labels   = this.ventasTimeline.map(t => t.fecha);
        const diario   = this.ventasTimeline.map(t => t.cantidadDiaria);
        const acum     = this.ventasTimeline.map(t => t.cantidadAcumulada);
        this.charts.ventasTimeline = new Chart(ctxVT, {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: this.$t('dm_chart_label_ventas_diario'),
                data: diario,
                borderColor: '#EF4444',
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 3,
                fill: false,
                yAxisID: 'y-diario',
              },
              {
                label: this.$t('dm_chart_label_ventas_acum'),
                data: acum,
                borderColor: '#10B981',
                backgroundColor: 'rgba(16,185,129,0.12)',
                borderWidth: 2,
                pointRadius: 3,
                fill: true,
                yAxisID: 'y-acum',
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { position: 'bottom', labels: { boxWidth: 24, padding: 16, fontSize: 11 } },
            scales: {
              xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 12, fontSize: 10, fontColor: '#6b7280' }, gridLines: { display: false } }],
              yAxes: [
                { id: 'y-diario', position: 'left',  ticks: { beginAtZero: true, fontSize: 10, fontColor: '#EF4444' }, gridLines: { color: '#f3f4f6' } },
                { id: 'y-acum',   position: 'right', ticks: { beginAtZero: true, fontSize: 10, fontColor: '#10B981' }, gridLines: { drawOnChartArea: false } },
              ],
            },
          },
        });
      }

      // ── Estado de publicación (doughnut) ─────────────────────────────────
      this.destroyChart('estadoPublicacion');
      const ctxEP = this.$refs.chartEstadoPublicacion;
      if (ctxEP && this.kpis) {
        const publicados    = this.kpis.itemsPublicados || 0;
        const noPublicados  = Math.max(0, (this.kpis.itemsTotales || 0) - publicados);
        const totalPub = publicados + noPublicados;
        this.charts.estadoPublicacion = new Chart(ctxEP, {
          type: 'doughnut',
          data: {
            labels: [this.$t('dm_items_publicados'), this.$t('dm_items_no_publicados')],
            datasets: [{
              data: [publicados, noPublicados],
              backgroundColor: ['#10B981', '#F59E0B'],
              borderWidth: 2,
              borderColor: '#fff',
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: {
                fontSize: 11,
                padding: 14,
                boxWidth: 14,
                generateLabels: (chart) => {
                  const d = chart.data;
                  return d.labels.map((label, i) => {
                    const val = d.datasets[0].data[i];
                    const pct = totalPub > 0 ? Math.round(val / totalPub * 1000) / 10 : 0;
                    return {
                      text: `${label}: ${Number(val).toLocaleString(this.numLocale())} (${pct}%)`,
                      fillStyle: d.datasets[0].backgroundColor[i],
                      strokeStyle: '#fff',
                      lineWidth: 1,
                      hidden: false,
                      index: i,
                    };
                  });
                },
              },
            },
            tooltips: {
              callbacks: {
                label: (item, data) => {
                  const val = data.datasets[0].data[item.index];
                  const pct = totalPub > 0 ? Math.round(val / totalPub * 1000) / 10 : 0;
                  return ` ${data.labels[item.index]}: ${Number(val).toLocaleString(this.numLocale())} (${pct}%)`;
                },
              },
            },
          },
        });
      }

      // Horizontal bar: Top fabricantes por valor (fila 3)
      this.destroyChart('marcas');
      const ctxMr = this.$refs.chartMarcas;
      if (ctxMr && this.marcas.length) {
        const fmtUSD = v => `$${Math.round(v).toLocaleString(this.numLocale())} USD`;
        const fmtAxis = v => {
          if (v === 0) return '$0 USD';
          if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(1)}B USD`;
          if (Math.abs(v) >= 1e6) return `$${Math.round(v / 1e6).toLocaleString(this.numLocale())}M USD`;
          return `$${Math.round(v).toLocaleString(this.numLocale())} USD`;
        };
        this.charts.marcas = new Chart(ctxMr, {
          type: 'horizontalBar',
          data: {
            labels: this.marcas.map(m => m.marca),
            datasets: [{
              label: this.$t('dm_chart_label_valor_stock_s'),
              data: this.marcas.map(m => m.valorTotal),
              backgroundColor: 'rgba(248,113,113,0.85)',
              borderColor: 'rgba(239,68,68,1)',
              borderWidth: 1,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            scales: {
              xAxes: [{
                ticks: { fontSize: 10, fontColor: '#6b7280', callback: v => fmtAxis(v) },
                gridLines: { color: '#f3f4f6' },
              }],
              yAxes: [{
                ticks: { fontSize: 11, fontColor: '#374151' },
                gridLines: { display: false },
              }],
            },
            tooltips: {
              callbacks: { label: item => ` ${this.$t('dm_tooltip_stock')} ${fmtUSD(item.xLabel)}` },
            },
          },
        });
      }
    },


    _buildEquispacedTimeline(subset, mode) {
      if (!subset.length) return { labels: [], datasets: [] };

      // Eje de fechas continuo (equiespaciado)
      const allDates = subset.map(t => t.fecha).filter(Boolean);
      if (!allDates.length) return { labels: [], datasets: [] };
      const minD = allDates.reduce((a, b) => a < b ? a : b);
      const maxD = allDates.reduce((a, b) => a > b ? a : b);
      const dateAxis = [];
      const cur = new Date(minD + 'T00:00:00');
      const end = new Date(maxD + 'T00:00:00');
      while (cur <= end) {
        dateAxis.push(cur.toISOString().split('T')[0]);
        cur.setDate(cur.getDate() + 1);
      }

      // Una serie por cliente
      const clients = [...new Set(subset.map(t => t.nombreCliente))];
      const curveColors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

      const datasets = clients.map((client, idx) => {
        const clientRows = subset.filter(t => t.nombreCliente === client);
        const dailyMap = {};
        clientRows.forEach(t => {
          dailyMap[t.fecha] = (dailyMap[t.fecha] || 0) + (mode === 'VALUE' ? Number(t.valorTotal) : t.cantidad);
        });

        // Acumulado equiespaciado: mantiene el último valor conocido
        let acum = 0;
        const clientMin = clientRows.map(t => t.fecha).reduce((a, b) => a < b ? a : b, '');
        const cumulativeData = dateAxis.map(d => {
          if (d < clientMin) return null;
          acum += (dailyMap[d] || 0);
          return acum;
        });

        return {
          label: mode === 'VALUE' ? `${client} (Acum.)` : `${client} (Filas Acum.)`,
          data: cumulativeData,
          borderColor: curveColors[idx % curveColors.length],
          backgroundColor: 'transparent',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 3,
          spanGaps: true,
          fill: false,
        };
      });

      return { labels: dateAxis, datasets };
    },

    renderTimelineChart() {
      if (!this.timeline.length) return;

      const commonOptions = (titleY, isCLP) => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'bottom', labels: { fontSize: 10, fontColor: '#6b7280', boxWidth: 12 } },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: item => {
              const v = Number(item.yLabel);
              return isCLP
                ? ` ${item.datasetLabel}: $${Math.round(v).toLocaleString(this.numLocale())} USD`
                : ` ${item.datasetLabel}: ${Math.round(v).toLocaleString(this.numLocale())} filas`;
            },
          },
        },
        scales: {
          xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 14, fontSize: 9, fontColor: '#6b7280', maxRotation: 45 }, gridLines: { display: false } }],
          yAxes: [{
            ticks: {
              fontSize: 9,
              fontColor: '#6b7280',
              callback: v => isCLP ? `$${(v / 1e6).toFixed(1)}M` : v.toLocaleString(this.numLocale()),
            },
            scaleLabel: { display: true, labelString: titleY, fontSize: 9, fontColor: '#9ca3af' },
          }],
        },
      });

      // Chart Volumen (Cantidad)
      this.destroyChart('timelineVolume');
      const ctxV = this.$refs.chartTimelineVolume;
      if (ctxV) {
        const { labels, datasets } = this._buildEquispacedTimeline(this.timeline, 'VOLUME');
        this.charts.timelineVolume = new Chart(ctxV, {
          type: 'line',
          data: { labels, datasets },
          options: commonOptions('Filas Acumuladas', false),
        });
      }

      // Chart Valor ($)
      this.destroyChart('timelineValue');
      const ctxVal = this.$refs.chartTimelineValue;
      if (ctxVal) {
        const { labels, datasets } = this._buildEquispacedTimeline(this.timeline, 'VALUE');
        this.charts.timelineValue = new Chart(ctxVal, {
          type: 'line',
          data: { labels, datasets },
          options: commonOptions('Valor Acumulado', true),
        });
      }
    },

    renderRfmCharts() {
      const data = this.rfmData;
      if (!Chart || !Array.isArray(data) || !data.length) return;

      const byRecency   = [...data].sort((a, b) => b.recency  - a.recency);
      const byFrequency = [...data].sort((a, b) => b.frequency - a.frequency);
      const byMonetary  = [...data].sort((a, b) => (b.monetary || 0) - (a.monetary || 0));

      const colorOf = r => r.estado === 'Activo' ? '#10b981' : r.estado === 'En Riesgo' ? '#f59e0b' : '#ef4444';

      const hBarOpts = (unit, labelFn) => {
        const ticksOpts = { beginAtZero: true, fontSize: 10, fontColor: '#6b7280' };
        if (typeof labelFn === 'function') ticksOpts.callback = labelFn;
        return {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ ticks: ticksOpts, gridLines: { color: '#f3f4f6' } }],
            yAxes: [{ ticks: { fontSize: 10, fontColor: '#374151' }, gridLines: { display: false } }],
          },
          tooltips: {
            callbacks: {
              label: item => ` ${typeof labelFn === 'function' ? labelFn(item.xLabel) : item.xLabel} ${unit}`,
            },
          },
        };
      };

      // ── 1. Recencia (horizontal bar — rojo) ──────────────────────────────
      this.destroyChart('rfmRecency');
      const ctxR = this.$refs.chartRfmRecency;
      if (ctxR) {
        this.charts.rfmRecency = new Chart(ctxR, {
          type: 'horizontalBar',
          data: {
            labels: byRecency.map(r => r.nombreCliente),
            datasets: [{
              label: this.$t('rfm_dias'),
              data: byRecency.map(r => r.recency === 9999 ? 0 : r.recency),
              backgroundColor: byRecency.map(r => colorOf(r) + 'cc'),
              borderColor: byRecency.map(r => colorOf(r)),
              borderWidth: 1,
            }],
          },
          options: hBarOpts(this.$t('rfm_dias')),
        });
      }

      // ── 2. Frecuencia (horizontal bar — azul) ────────────────────────────
      this.destroyChart('rfmFrequency');
      const ctxF = this.$refs.chartRfmFrequency;
      if (ctxF) {
        this.charts.rfmFrequency = new Chart(ctxF, {
          type: 'horizontalBar',
          data: {
            labels: byFrequency.map(r => r.nombreCliente),
            datasets: [{
              label: this.$t('rfm_archivos'),
              data: byFrequency.map(r => r.frequency),
              backgroundColor: 'rgba(59,130,246,0.65)',
              borderColor: '#3b82f6',
              borderWidth: 1,
            }],
          },
          options: hBarOpts(this.$t('rfm_archivos')),
        });
      }

      // ── 3. Monetary / Stock valorizado (horizontal bar — verde) ──────────
      this.destroyChart('rfmMonetary');
      const ctxM = this.$refs.chartRfmMonetary;
      if (ctxM) {
        const fmtUSD = v => {
          if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
          if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
          if (v >= 1e3) return `$${Math.round(v/1e3)}K`;
          return `$${Math.round(v)}`;
        };
        this.charts.rfmMonetary = new Chart(ctxM, {
          type: 'horizontalBar',
          data: {
            labels: byMonetary.map(r => r.nombreCliente),
            datasets: [{
              label: 'Stock (USD)',
              data: byMonetary.map(r => r.monetary || 0),
              backgroundColor: 'rgba(16,185,129,0.65)',
              borderColor: '#10b981',
              borderWidth: 1,
            }],
          },
          options: hBarOpts('USD', fmtUSD),
        });
      }

      // ── 4. Donut Churn Risk ───────────────────────────────────────────────
      this.destroyChart('rfmDonut');
      const ctxD = this.$refs.chartRfmDonut;
      if (ctxD) {
        const active   = this.rfmActivos;
        const risk     = this.rfmRiesgo;
        const inactive = this.rfmInactivos;
        const total    = active + risk + inactive;
        this.charts.rfmDonut = new Chart(ctxD, {
          type: 'doughnut',
          data: {
            labels: [this.$t('rfm_seg_activo_label'), this.$t('rfm_seg_riesgo_label'), this.$t('rfm_seg_inactivo_label')],
            datasets: [{
              data: [active, risk, inactive],
              backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
              borderWidth: 2,
              borderColor: '#fff',
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: { fontSize: 11, padding: 14, boxWidth: 12 },
            },
            tooltips: {
              callbacks: {
                label: (tooltipItem, chartData) => {
                  const val = chartData.datasets[0].data[tooltipItem.index];
                  const pct = total > 0 ? (val / total * 100).toFixed(1) : 0;
                  return ` ${chartData.labels[tooltipItem.index]}: ${val} (${pct}%)`;
                },
              },
            },
          },
        });
      }

      // ── 5. Scatter 3D + forzar resize de charts (panel puede venir de display:none) ──
      this.$nextTick(() => {
        ['rfmRecency', 'rfmFrequency', 'rfmMonetary', 'rfmDonut'].forEach(k => {
          try { if (this.charts[k]) this.charts[k].resize(); } catch (_) {}
        });

        const snapshot = this.rfmData;
        if (!Array.isArray(snapshot) || !snapshot.length) return;
        const rVals = snapshot.map(r => r.recency === 9999 ? 0 : (r.recency || 0));
        const fVals = snapshot.map(r => r.frequency || 0);
        const mVals = snapshot.map(r => r.monetary  || 0);
        const maxR = Math.max.apply(null, rVals.concat(1));
        const maxF = Math.max.apply(null, fVals.concat(1));
        const maxM = Math.max.apply(null, mVals.concat(1));
        const B = 0.65;
        this.rfm3dPoints = snapshot.map((r, i) => ({
          name:      r.nombreCliente || '?',
          x:         (rVals[i] / maxR) * B,
          y:        -((fVals[i] / maxF) * B),
          z:         (mVals[i] / maxM) * B,
          status:    r.estado,
          recency:   rVals[i],
          frequency: fVals[i],
          monetary:  mVals[i],
        }));
        this.draw3D();
      });
    },

    // ──────────────── 3D Scatter helpers ─────────────────────────────────────
    rfmRotX(x, y, z, deg) {
      const rad = deg * Math.PI / 180;
      const c = Math.cos(rad); const s = Math.sin(rad);
      return { x, y: y * c - z * s, z: y * s + z * c };
    },
    rfmRotY(x, y, z, deg) {
      const rad = deg * Math.PI / 180;
      const c = Math.cos(rad); const s = Math.sin(rad);
      return { x: x * c + z * s, y, z: -x * s + z * c };
    },
    rfmProj(x, y, z, W, H) {
      const dist = 2.5;
      const f = this.rfm3dScale / (z + dist);
      return { x: W / 2 + (x - 0.35) * f, y: H / 2 + (y + 0.35) * f };
    },
    draw3D() {
      const canvas = this.$refs.rfm3dCanvas;
      if (!canvas) return;
      // Fit to container
      const wrap = canvas.parentElement;
      if (wrap && wrap.clientWidth > 0) {
        canvas.width  = wrap.clientWidth - 16;
        canvas.height = Math.round((wrap.clientWidth - 16) * 250 / 550);
      }
      const ctx = canvas.getContext('2d');
      const W = canvas.width; const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      // project helper bound to current rotation state
      const proj = (x, y, z) => {
        const r1 = this.rfmRotX(x, y, z, this.rfm3dRotationX);
        const r2 = this.rfmRotY(r1.x, r1.y, r1.z, this.rfm3dRotationY);
        return this.rfmProj(r2.x, r2.y, r2.z, W, H);
      };

      // Wireframe box
      const B = 0.65;
      const verts = [
        [0,0,0],[B,0,0],[B,-B,0],[0,-B,0],
        [0,0,B],[B,0,B],[B,-B,B],[0,-B,B],
      ].map(([x,y,z]) => proj(x,y,z));
      const edges = [[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7]];
      ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 0.8;
      ctx.setLineDash([3,3]);
      edges.forEach(([i,j]) => {
        ctx.beginPath();
        ctx.moveTo(verts[i].x, verts[i].y);
        ctx.lineTo(verts[j].x, verts[j].y);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      // Axes
      const origin = proj(0,0,0);
      const axisLines = [
        { to: proj(B*1.08,0,0), color:'#ef4444', label:'Recency (R)' },
        { to: proj(0,-B*1.08,0), color:'#3b82f6', label:'Frequency (F)' },
        { to: proj(0,0,B*1.08), color:'#10b981', label:'Monetary (M)' },
      ];
      axisLines.forEach(({ to, color, label }) => {
        ctx.strokeStyle = color; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(origin.x, origin.y); ctx.lineTo(to.x, to.y); ctx.stroke();
        ctx.fillStyle = color;
        ctx.font = 'bold 9px Inter,sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(label, to.x, to.y - 5);
      });

      // Points (sorted back-to-front)
      const colored = this.rfm3dPoints.map(pt => {
        const r1 = this.rfmRotX(pt.x, pt.y, pt.z, this.rfm3dRotationX);
        const r2 = this.rfmRotY(r1.x, r1.y, r1.z, this.rfm3dRotationY);
        return { ...pt, rz: r2.z, p: this.rfmProj(r2.x, r2.y, r2.z, W, H) };
      }).sort((a, b) => a.rz - b.rz);

      const fmtM = v => {
        if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
        if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
        if (v >= 1e3) return `$${Math.round(v/1e3)}K`;
        return `$${Math.round(v)}`;
      };

      ctx.textAlign = 'center';
      colored.forEach(pt => {
        const color = pt.status === 'Activo' ? '#10b981' : pt.status === 'En Riesgo' ? '#f59e0b' : '#ef4444';
        const r = 11;
        ctx.beginPath();
        ctx.arc(pt.p.x, pt.p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = color + 'cc'; ctx.fill();
        ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.stroke();

        const label = pt.name.length > 12 ? pt.name.slice(0, 11) + '…' : pt.name;
        ctx.fillStyle = '#374151';
        ctx.font = '8px Inter,sans-serif';
        ctx.fillText(label, pt.p.x, pt.p.y + r + 9);
        ctx.fillStyle = '#9ca3af';
        ctx.font = '7px Inter,sans-serif';
        ctx.fillText(`R:${pt.recency}d F:${pt.frequency} M:${fmtM(pt.monetary)}`, pt.p.x, pt.p.y + r + 18);
      });
    },
    rfm3dMouseDown(e) { this.rfm3dDragging = true; this.rfm3dPrevX = e.clientX; this.rfm3dPrevY = e.clientY; },
    rfm3dMouseMove(e) {
      if (!this.rfm3dDragging) return;
      this.rfm3dRotationY += (e.clientX - this.rfm3dPrevX) * 0.5;
      this.rfm3dRotationX  = Math.max(-80, Math.min(80, this.rfm3dRotationX + (e.clientY - this.rfm3dPrevY) * 0.5));
      this.rfm3dPrevX = e.clientX; this.rfm3dPrevY = e.clientY;
      this.draw3D();
    },
    rfm3dMouseUp() { this.rfm3dDragging = false; },
    rfm3dWheel(e) {
      this.rfm3dScale = e.deltaY > 0 ? Math.max(70, this.rfm3dScale - 14) : Math.min(400, this.rfm3dScale + 14);
      this.rfm3dZoomPct = Math.round(this.rfm3dScale / 1.6);
      this.draw3D();
    },
    rfm3dZoomChange(val) {
      this.rfm3dScale = Number(val) * 1.6;
      this.draw3D();
    },

    renderAlertasChart() {
      if (!this.alertas.length) return;
      this.destroyChart('alertas');
      const ctx = this.$refs.chartAlertas;
      if (!ctx) return;
      this.charts.alertas = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.alertas.map(a => a.categoria),
          datasets: [{
            data: this.alertas.map(a => a.cantidad),
            backgroundColor: this.alertas.map((a, i) =>
              a.tipo === 'critical' ? '#FF638488' : COLORS[i % COLORS.length] + 'BB'
            ),
          }],
        },
        options: { responsive: true, maintainAspectRatio: false },
      });
    },

    renderClasificacionCharts() {
      if (!Chart || this.idEmpresaSeleccionada !== 0) return;

      // Horizontal bar helper
      const hBar = (ctx, labels, data, color) => new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels,
          datasets: [{ label: 'Items', data, backgroundColor: color + 'bf', borderColor: color, borderWidth: 1 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ ticks: { fontSize: 10, fontColor: '#6b7280' }, gridLines: { color: '#f3f4f6' } }],
            yAxes: [{ ticks: { fontSize: 10, fontColor: '#374151' }, gridLines: { display: false } }],
          },
          tooltips: { callbacks: { label: item => ` ${Number(item.xLabel).toLocaleString(this.numLocale())} artículos` } },
        },
      });

      // Chart 1: Categorías (top 6)
      this.destroyChart('clasifCategorias');
      const ctxCat = this.$refs.chartClasifCategorias;
      if (ctxCat && this.categorias.length) {
        const top6 = this.categorias.slice(0, 6);
        this.charts.clasifCategorias = hBar(
          ctxCat,
          top6.map(c => this.truncate(c.categoria, 28)),
          top6.map(c => c.totalArticulos),
          '#00A3E0'
        );
      }

      // Chart 2: Familias (top 6)
      this.destroyChart('clasifFamilias');
      const ctxFam = this.$refs.chartClasifFamilias;
      if (ctxFam && this.familias.length) {
        const top6f = this.familias.slice(0, 6);
        this.charts.clasifFamilias = hBar(
          ctxFam,
          top6f.map(f => this.truncate(f.familia, 28)),
          top6f.map(f => f.totalArticulos),
          '#3b82f6'
        );
      }

      // Chart 3: Clases (top 6)
      this.destroyChart('clasifClases');
      const ctxCls = this.$refs.chartClasifClases;
      if (ctxCls && this.clases.length) {
        const top6c = this.clases.slice(0, 6);
        this.charts.clasifClases = hBar(
          ctxCls,
          top6c.map(c => this.truncate(c.clase, 28)),
          top6c.map(c => c.totalArticulos),
          '#f97316'
        );
      }

      // Chart 4: Segmentos (top 6)
      this.destroyChart('clasifSegmentos');
      const ctxSeg = this.$refs.chartClasifSegmentos;
      if (ctxSeg && this.segmentos.length) {
        const top6s = this.segmentos.slice(0, 6);
        this.charts.clasifSegmentos = hBar(
          ctxSeg,
          top6s.map(s => this.truncate(s.segmento, 28)),
          top6s.map(s => s.totalArticulos),
          '#6366f1'
        );
      }

      // Chart 5: KDE por clientes (líneas)
      this.destroyChart('clasifKde');
      const ctxKde = this.$refs.chartClasifKde;
      const kdeDist = this.confianzaDist;
      const binLabels = ['0-10%', '10-20%', '20-30%', '30-40%', '40-50%', '50-60%', '60-70%', '70-80%', '80-90%', '90-100%'];
      const kdeColors = ['#00A3E0', '#f97316', '#10b981', '#8b5cf6'];
      if (ctxKde && kdeDist && kdeDist.clientesKde && kdeDist.clientesKde.length) {
        this.charts.clasifKde = new Chart(ctxKde, {
          type: 'line',
          data: {
            labels: binLabels,
            datasets: kdeDist.clientesKde.map((c, i) => ({
              label: this.truncate(c.nombreCliente, 20),
              data: c.densidades,
              borderColor: kdeColors[i % kdeColors.length],
              backgroundColor: kdeColors[i % kdeColors.length] + '22',
              fill: true,
              borderWidth: 2,
              pointRadius: 3,
              tension: 0.4,
            })),
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: true, position: 'top', labels: { fontSize: 10, boxWidth: 12 } },
            scales: {
              xAxes: [{ ticks: { fontSize: 10, fontColor: '#6b7280' }, gridLines: { color: '#f3f4f6' } }],
              yAxes: [{ ticks: { fontSize: 10, fontColor: '#6b7280', callback: v => `${v}%` }, gridLines: { color: '#f3f4f6' } }],
            },
            tooltips: { callbacks: { label: (item, data) => ` ${data.datasets[item.datasetIndex].label}: ${Number(item.yLabel).toFixed(1)}%` } },
          },
        });
      }

      // Chart 6: KDE global (barras + línea)
      this.destroyChart('clasifKdeGeneral');
      const ctxKdeG = this.$refs.chartClasifKdeGeneral;
      if (ctxKdeG && kdeDist && kdeDist.globalKde && kdeDist.globalKde.length) {
        this.charts.clasifKdeGeneral = new Chart(ctxKdeG, {
          type: 'bar',
          data: {
            labels: binLabels,
            datasets: [
              {
                label: 'Frecuencia (%)',
                data: kdeDist.globalBins,
                backgroundColor: '#00A3E044',
                borderColor: '#00A3E0',
                borderWidth: 1,
                yAxisID: 'y-bar',
              },
              {
                label: 'KDE Suavizado',
                data: kdeDist.globalKde,
                type: 'line',
                borderColor: '#f97316',
                backgroundColor: 'transparent',
                borderWidth: 2.5,
                pointRadius: 2,
                fill: false,
                tension: 0.4,
                yAxisID: 'y-bar',
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: true, position: 'top', labels: { fontSize: 10, boxWidth: 12 } },
            scales: {
              xAxes: [{ ticks: { fontSize: 10, fontColor: '#6b7280' }, gridLines: { color: '#f3f4f6' } }],
              yAxes: [{
                id: 'y-bar',
                ticks: { fontSize: 10, fontColor: '#6b7280', callback: v => `${v}%` },
                gridLines: { color: '#f3f4f6' },
              }],
            },
            tooltips: { callbacks: { label: (item, data) => ` ${data.datasets[item.datasetIndex].label}: ${Number(item.yLabel).toFixed(1)}%` } },
          },
        });
      }
    },

    // ── Helpers ──────────────────────────────────────────────────────────

    numLocale() {
      return this.lang === 'en' ? 'en-US' : 'es-CL';
    },

    formatCLP(value) {
      if (value == null || isNaN(value)) return '—';
      return new Intl.NumberFormat(this.numLocale(), { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value);
    },

    formatUSD(value) {
      if (value == null || isNaN(value)) return '—';
      const abs = Math.abs(Number(value));
      const formatted = new Intl.NumberFormat(this.numLocale(), { maximumFractionDigits: 0 }).format(abs);
      return (Number(value) < 0 ? '-' : '') + '$' + formatted + ' USD';
    },

    formatUSDDecimal(value) {
      if (value == null || isNaN(value)) return '—';
      const abs = Math.abs(Number(value));
      const formatted = new Intl.NumberFormat(this.numLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(abs);
      return (Number(value) < 0 ? '-' : '') + '$' + formatted + ' USD';
    },

    renderMonitoreoCharts() {
      if (!Chart || !this.ventasMonitoreo) return;

      const MON_COLORS = [
        '#3B82F6', '#10B981', '#F97316', '#EC4899', '#8B5CF6',
        '#06B6D4', '#F59E0B', '#EF4444', '#84CC16', '#6B7280',
      ];

      const formatMonAxis = v => {
        if (v === 0) return '$0';
        if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
        if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
        if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
        return `$${Math.round(v).toLocaleString(this.numLocale())}`;
      };

      // ── Donut: Ventas por Categoría ──────────────────────────────────────
      this.destroyChart('monitoreoCategoria');
      const ctxCat = this.$refs.chartMonitoreoCategoria;
      if (ctxCat && this.ventasMonitoreo.ventasPorCategoria && this.ventasMonitoreo.ventasPorCategoria.length) {
        const data = this.ventasMonitoreo.ventasPorCategoria.slice(0, 10);
        const catLabels = data.map(d => d.categoria);
        const catValues = data.map(d => d.montoVenta);
        const totalCat = catValues.reduce((a, b) => a + b, 0);
        this.charts.monitoreoCategoria = new Chart(ctxCat, {
          type: 'doughnut',
          data: {
            labels: catLabels,
            datasets: [{
              data: catValues,
              backgroundColor: MON_COLORS.slice(0, data.length),
              borderWidth: 2,
              borderColor: '#fff',
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: { boxWidth: 12, padding: 12, fontSize: 10, fontColor: '#6b7280' },
            },
            tooltips: {
              callbacks: {
                label: (item, chartData) => {
                  const val = chartData.datasets[0].data[item.index];
                  const pct = totalCat > 0 ? ((val / totalCat) * 100).toFixed(1) : '0.0';
                  return ` ${chartData.labels[item.index]}: ${this.formatUSD(val)} (${pct}%)`;
                },
              },
            },
          },
        });
      }

      // ── Line: Evolución Temporal Monetaria ───────────────────────────────
      this.destroyChart('monitoreoTimeline');
      const ctxTL = this.$refs.chartMonitoreoTimeline;
      if (ctxTL && this.ventasMonitoreo.timeline && this.ventasMonitoreo.timeline.length) {
        const tlLabels = this.ventasMonitoreo.timeline.map(t => t.fecha);
        const tlDiario  = this.ventasMonitoreo.timeline.map(t => t.montoVentaDiaria);
        const tlAcum    = this.ventasMonitoreo.timeline.map(t => t.montoAcumulado);
        this.charts.monitoreoTimeline = new Chart(ctxTL, {
          type: 'line',
          data: {
            labels: tlLabels,
            datasets: [
              {
                label: this.$t('mon_legend_diario'),
                data: tlDiario,
                borderColor: '#EF4444',
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 3,
                pointBackgroundColor: '#EF4444',
                fill: false,
                yAxisID: 'y-diario',
              },
              {
                label: this.$t('mon_legend_acum'),
                data: tlAcum,
                borderColor: '#3B82F6',
                backgroundColor: 'rgba(59,130,246,0.07)',
                borderWidth: 2,
                pointRadius: 4,
                pointBackgroundColor: '#3B82F6',
                fill: false,
                yAxisID: 'y-acum',
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { position: 'bottom', labels: { boxWidth: 24, padding: 16, fontSize: 11 } },
            scales: {
              xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 12, fontSize: 10, fontColor: '#6b7280' }, gridLines: { display: false } }],
              yAxes: [
                {
                  id: 'y-diario',
                  position: 'left',
                  scaleLabel: { display: true, labelString: this.$t('mon_legend_diario'), fontColor: '#EF4444', fontSize: 10 },
                  ticks: { beginAtZero: true, fontSize: 10, fontColor: '#EF4444', callback: v => formatMonAxis(v) },
                  gridLines: { color: '#f3f4f6' },
                },
                {
                  id: 'y-acum',
                  position: 'right',
                  scaleLabel: { display: true, labelString: this.$t('mon_legend_acum'), fontColor: '#3B82F6', fontSize: 10 },
                  ticks: { beginAtZero: true, fontSize: 10, fontColor: '#3B82F6', callback: v => formatMonAxis(v) },
                  gridLines: { drawOnChartArea: false },
                },
              ],
            },
            tooltips: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (item, chartData) => {
                  const ds = chartData.datasets[item.datasetIndex];
                  return ` ${ds.label}: ${this.formatUSD(item.yLabel)}`;
                },
              },
            },
          },
        });
      }
    },

    truncate(str, n) {
      return str && str.length > n ? str.slice(0, n - 1) + '…' : str;
    },

    confianzaClass(v) {
      if (v >= 85) return 'text-success font-weight-bold';
      if (v >= 70) return 'text-info';
      if (v >= 50) return 'text-warning';
      return 'text-danger';
    },

    rfmTagType(estado) {
      return estado === 'Activo' ? 'success' : estado === 'En Riesgo' ? 'warning' : 'danger';
    },

    // ── Charts vista cliente ──────────────────────────────────────

    renderClienteCharts() {
      if (!Chart) return;

      // 1. Distribución de valor por categoría (doughnut)
      this.destroyChart('clienteValorCat');
      const ctxVC = this.$refs.chartClienteValorCat;
      if (ctxVC && this.categorias.length) {
        const top7 = this.categorias.slice(0, 7);
        const COLORS7 = ['#5B8DEF','#10B981','#F97316','#EC4899','#8B5CF6','#FBBF24','#06B6D4'];
        this.charts.clienteValorCat = new Chart(ctxVC, {
          type: 'doughnut',
          data: {
            labels: top7.map(c => c.categoria.toUpperCase()),
            datasets: [{ data: top7.map(c => c.valorTotal), backgroundColor: COLORS7, borderWidth: 2, borderColor: '#fff' }],
          },
          options: {
            responsive: true, maintainAspectRatio: false, cutoutPercentage: 62,
            legend: { position: 'bottom', labels: { fontSize: 10, padding: 10, boxWidth: 12 } },
            tooltips: { callbacks: { label: (item, data) => {
              const v = data.datasets[0].data[item.index];
              return ` ${data.labels[item.index]}: $${Math.round(v).toLocaleString(this.numLocale())}`;
            }}},
          },
        });
      }

      // 2. Estado de publicación del catálogo (doughnut publicados vs no publicados)
      this.destroyChart('clienteCatalogacion');
      const ctxCat = this.$refs.chartClienteCatalogacion;
      if (ctxCat && this.kpis) {
        const pub    = this.kpis.itemsPublicados || 0;
        const noPub  = Math.max(0, (this.kpis.itemsTotales || 0) - pub);
        const totalC = pub + noPub;
        this.charts.clienteCatalogacion = new Chart(ctxCat, {
          type: 'doughnut',
          data: {
            labels: [this.$t('dm_items_publicados'), this.$t('dm_items_no_publicados')],
            datasets: [{ data: [pub, noPub],
              backgroundColor: ['#10B981', '#F59E0B'], borderWidth: 2, borderColor: '#fff' }],
          },
          options: {
            responsive: true, maintainAspectRatio: false, cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: {
                fontSize: 11, padding: 14, boxWidth: 14,
                generateLabels: (chart) => {
                  const d = chart.data;
                  return d.labels.map((label, i) => {
                    const val = d.datasets[0].data[i];
                    const pct = totalC > 0 ? Math.round(val / totalC * 1000) / 10 : 0;
                    return {
                      text: `${label}: ${Number(val).toLocaleString(this.numLocale())} (${pct}%)`,
                      fillStyle: d.datasets[0].backgroundColor[i],
                      strokeStyle: '#fff', lineWidth: 1, hidden: false, index: i,
                    };
                  });
                },
              },
            },
            tooltips: {
              callbacks: {
                label: (item, data) => {
                  const val = data.datasets[0].data[item.index];
                  const pct = totalC > 0 ? Math.round(val / totalC * 1000) / 10 : 0;
                  return ` ${data.labels[item.index]}: ${Number(val).toLocaleString(this.numLocale())} (${pct}%)`;
                },
              },
            },
          },
        });
      }

      // 3. Top 5 fabricantes (horizontal bar)
      this.destroyChart('clienteMarcas');
      const ctxMr = this.$refs.chartClienteMarcas;
      if (ctxMr && this.marcas.length) {
        const top5 = this.marcas.slice(0, 5);
        const fmtUSD  = v => `$${Math.round(v).toLocaleString(this.numLocale())} USD`;
        const fmtAxis = v => {
          if (v === 0) return '$0 USD';
          if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(1)}B USD`;
          if (Math.abs(v) >= 1e6) return `$${Math.round(v / 1e6).toLocaleString(this.numLocale())}M USD`;
          return `$${Math.round(v).toLocaleString(this.numLocale())} USD`;
        };
        this.charts.clienteMarcas = new Chart(ctxMr, {
          type: 'horizontalBar',
          data: {
            labels: top5.map(m => m.marca),
            datasets: [{ label: 'Stock', data: top5.map(m => m.valorTotal),
              backgroundColor: 'rgba(236,72,153,0.8)', borderColor: 'rgba(236,72,153,1)', borderWidth: 1 }],
          },
          options: {
            responsive: true, maintainAspectRatio: false, legend: { display: false },
            scales: {
              xAxes: [{ ticks: { fontSize: 10, fontColor: '#6b7280', callback: v => fmtAxis(v) }, gridLines: { color: '#f3f4f6' } }],
              yAxes: [{ ticks: { fontSize: 11, fontColor: '#374151' }, gridLines: { display: false } }],
            },
            tooltips: { callbacks: { label: item => ` ${this.$t('dm_tooltip_stock')} ${fmtUSD(item.xLabel)}` } },
          },
        });
      }

      // 4. Clase de materiales (doughnut)
      this.destroyChart('clienteClase');
      const ctxCl = this.$refs.chartClienteClase;
      if (ctxCl && this.clases.length) {
        const CLASE_COLORS = ['#F97316','#5B8DEF','#10B981','#EC4899','#8B5CF6','#FBBF24'];
        const claseRegistrosLabel = this.$t('dm_tooltip_registros');
        const locale = this.numLocale();
        this.charts.clienteClase = new Chart(ctxCl, {
          type: 'doughnut',
          data: {
            labels: this.clases.map(c => c.clase.toUpperCase()),
            datasets: [{ data: this.clases.map(c => c.totalArticulos), backgroundColor: CLASE_COLORS, borderWidth: 2, borderColor: '#fff' }],
          },
          options: {
            responsive: true, maintainAspectRatio: false, cutoutPercentage: 62,
            legend: {
              position: 'bottom',
              labels: {
                fontSize: 10, padding: 10, boxWidth: 12,
                generateLabels: function(chart) {
                  return chart.data.labels.map((label, i) => ({
                    text: `${label}: ${Number(chart.data.datasets[0].data[i]).toLocaleString(locale)} ${claseRegistrosLabel}`,
                    fillStyle: CLASE_COLORS[i % CLASE_COLORS.length],
                    strokeStyle: '#fff',
                    lineWidth: 2,
                    hidden: false,
                    index: i,
                  }));
                },
              },
            },
            tooltips: { callbacks: { label: (item, data) => {
              const val = data.datasets[item.datasetIndex].data[item.index];
              return ` ${data.labels[item.index]}: ${Number(val).toLocaleString(this.numLocale())} ${this.$t('dm_tooltip_registros')}`;
            } } },
          },
        });
      }

      // 5. Optimización y ahorro por categoría (bar)
      this.destroyChart('clienteAhorro');
      const ctxAh = this.$refs.chartClienteAhorro;
      if (ctxAh && this.categorias.length) {
        const top6 = this.categorias.filter(c => c.ahorroPotencial !== 0).slice(0, 6);
        this.charts.clienteAhorro = new Chart(ctxAh, {
          type: 'bar',
          data: {
            labels: top6.map(c => this.truncate(c.categoria, 18)),
            datasets: [{ label: this.$t('dm_chart_label_ahorro_sugerido'), data: top6.map(c => Math.abs(c.ahorroPotencial)),
              backgroundColor: 'rgba(16,185,129,0.8)', borderColor: 'rgba(16,185,129,1)', borderWidth: 1 }],
          },
          options: {
            responsive: true, maintainAspectRatio: false, legend: { display: false },
            tooltips: { callbacks: { label: item => ` ${this.$t('dm_tooltip_ahorro_sugerido')} $${Math.round(item.yLabel).toLocaleString(this.numLocale())}` } },
            scales: {
              xAxes: [{ ticks: { fontSize: 9 }, gridLines: { display: false } }],
              yAxes: [{ ticks: { fontSize: 10, callback: v => `$${Math.round(v/1e3).toLocaleString(this.numLocale())}K` }, gridLines: { color: '#f3f4f6' } }],
            },
          },
        });
      }
    },
  },
};
</script>

<style scoped>
.dashboard-master { padding: 0; }

.dm-header {
  background: #5b6abf;
  padding: 26px 28px 26px 64px;
  margin-bottom: 24px;
}
.dm-title { font-size: 1.2rem; font-weight: 700; color: #fff; }
.dm-header small { color: rgba(255,255,255,.75); font-size: 0.8rem; }

/* El resto del contenido con padding lateral */
.kpi-primary-grid,
.kpi-secondary-grid,
.dm-tab-content { margin-left: 24px; margin-right: 24px; }
.kpi-secondary-grid { margin-bottom: 20px; }

/* ── KPI Row 1: primarios ──────────────────────────────────── */
.kpi-primary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
.kpi-primary-card {
  position: relative;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 20px 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  cursor: default;
}
.kpi-primary-card:hover {
  border-color: #00A3E0;
  box-shadow: 0 0 0 2px rgba(0,163,224,.12), 0 2px 8px rgba(0,0,0,.08);
}
.kpi-primary-card--active {
  border-color: #00A3E0;
  box-shadow: 0 0 0 2px rgba(0,163,224,.15);
}
.kpi-watermark {
  position: absolute;
  top: 12px; right: 14px;
  font-size: 48px;
  color: #e5e7eb;
  pointer-events: none;
}
.kpi-p-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 6px;
}
.kpi-p-value {
  font-size: 1.55rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.15;
  margin-bottom: 4px;
}
.kpi-p-sub {
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 12px;
}
.kpi-p-sub--green { color: #10b981; }
.kpi-p-sub--red   { color: #ef4444; }
.kpi-p-sub--muted { color: #6b7280; }
.kpi-divider {
  border: none;
  border-top: 1px solid #f3f4f6;
  margin: 0 0 10px;
}
.kpi-section-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 5px;
}
.kpi-description {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

/* ── KPI Row 2: secundarios ────────────────────────────────── */
.kpi-secondary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
.kpi-secondary-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  cursor: default;
}
.kpi-secondary-card:hover {
  border-color: #00A3E0;
  box-shadow: 0 0 0 2px rgba(0,163,224,.12), 0 2px 8px rgba(0,0,0,.08);
}
.kpi-s-icon {
  width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
}
.kpi-s-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 2px;
}
.kpi-s-value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
  margin-bottom: 3px;
}
.kpi-s-sub { font-size: 0.73rem; color: #6b7280; line-height: 1.4; }

/* ── Barra de Tabs custom ────────────────────────────────── */
.dm-tab-bar {
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  margin-bottom: 20px;
}
.dm-tab-bar__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow-x: auto;
}
.dm-tab-btn {
  padding: 14px 4px;
  margin-right: 28px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  outline: none;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
  flex-shrink: 0;
}
.dm-tab-btn:hover:not(:disabled) { color: #374151; }
.dm-tab-btn--active {
  color: #00A3E0;
  border-bottom-color: #00A3E0;
}
.dm-tab-soon-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  background: #e0f2fe;
  color: #0369a1;
  vertical-align: middle;
  line-height: 1.6;
}
.dm-tab-btn--soon {
  cursor: default;
  opacity: 0.45;
}

/* Contenedor de contenido de tabs */
.dm-tab-content {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,.06);
  padding: 16px;
}

/* Chart grids */
.charts-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
/* Tab Resumen: chart ancho (≈62%) + tabla angosta (≈38%) */
.charts-grid-resumen {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;
}
/* Fila de 3 gráficos iguales */
.charts-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.charts-grid-1 { display: flex; flex-direction: column; gap: 16px; }

.chart-card {
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e8ecf0;
  padding: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.chart-card:hover {
  border-color: #00A3E0;
  box-shadow: 0 0 0 2px rgba(0,163,224,.12), 0 2px 8px rgba(0,0,0,.06);
}
.chart-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}
/* Contenedor de altura fija — evita que Chart.js crezca infinitamente */
.chart-wrap {
  position: relative;
  height: 280px;
}
.chart-wrap--tall {
  height: 340px;
}
.chart-wrap--kde {
  height: 300px;
}
.chart-wrap--lg {
  height: 360px;
}

/* Card featured (ocupa columna completa si se necesita) */
.chart-card--featured {
  background: #fff;
}

/* Header con título + badge */
.chart-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}
.chart-title--lg {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0;
}
.chart-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 3px;
}
.chart-badge {
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.72rem;
  color: #374151;
  font-family: monospace;
  white-space: nowrap;
}
.chart-badge--blue   { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.chart-badge--pink   { background: #fdf2f8; border-color: #f9a8d4; color: #9d174d; }
.chart-badge--green  { background: #ecfdf5; border-color: #6ee7b7; color: #065f46; }
.chart-badge--orange { background: #fff7ed; border-color: #fed7aa; color: #9a3412; }

/* Altura específica para doughnuts (necesitan espacio para la leyenda abajo) */
.chart-wrap--donut { height: 360px; }

/* Loading */
.dm-loading { text-align: center; padding: 60px 0; color: #666; font-size: 15px; }

/* Alertas */
.alertas-list { display: flex; flex-direction: column; gap: 8px; }
.alerta-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 12px; border-radius: 6px;
  font-size: 13px;
}
.alerta-critical { background: #fff1f0; border-left: 3px solid #ff4d4f; }
.alerta-warning { background: #fffbe6; border-left: 3px solid #faad14; }
.alerta-body { flex: 1; }
.alerta-body strong { display: block; font-weight: 600; }
.alerta-body span { color: #555; }

/* RFM badges */
.rfm-states { display: flex; gap: 10px; flex-wrap: wrap; }
.rfm-badge {
  padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 600;
}
.rfm-activo { background: #d1fae5; color: #065f46; }
.rfm-riesgo { background: #fef3c7; color: #92400e; }
.rfm-inactivo { background: #fee2e2; color: #991b1b; }

/* ── Desglose de registros ───────────────────────────────── */
.desgloce-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.desgloce-header { margin-bottom: 20px; }
.desgloce-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  text-align: center;
}
.desgloce-stat {
  padding: 12px 16px;
  border-right: 1px solid #f3f4f6;
}
.desgloce-stat:last-child { border-right: none; }
.desgloce-stat__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}
.desgloce-stat__value {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
}

/* ── Ranking table ────────────────────────────────────────── */
.ranking-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.ranking-table thead tr {
  border-bottom: 2px solid #f3f4f6;
}
.ranking-table thead th {
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  text-align: left;
}
.rt-col-num { text-align: right !important; }
.ranking-table tbody tr {
  border-bottom: 1px solid #f9fafb;
  transition: background 0.15s;
}
.ranking-table tbody tr:hover { background: #f9fafb; }
.rt-nombre {
  padding: 13px 12px;
  font-weight: 600;
  color: #111827;
}
.rt-conf {
  padding: 13px 12px;
  text-align: right;
  font-weight: 700;
  color: #00A3E0;
}
.rt-match {
  padding: 13px 12px;
  text-align: right;
  font-weight: 700;
  color: #10B981;
}

/* Ahorro potencial inline en toolbar */
.dm-ahorro-inline {
  display: inline-flex;
  align-items: center;
  background: #ecfdf5;
  color: #065f46;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #6ee7b7;
  white-space: nowrap;
}

/* Toolbar de filtros */
.dm-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 28px 10px 28px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
}
.dm-toolbar__left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dm-toolbar__label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.dm-select-native {
  height: 32px;
  padding: 0 28px 0 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  width: 240px;
  cursor: pointer;
  outline: none;
  appearance: auto;
}
.dm-select-native:focus { border-color: #409eff; }
.dm-toolbar__empresa-nombre {
  font-size: 13px;
  color: #303133;
  font-weight: 600;
  padding: 0 4px;
}

/* Responsive */
@media (max-width: 1200px) {
  .charts-grid-3 { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 1024px) {
  .kpi-primary-grid    { grid-template-columns: repeat(2, 1fr); }
  .kpi-secondary-grid  { grid-template-columns: repeat(2, 1fr); }
  .charts-grid-resumen { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .kpi-primary-grid    { grid-template-columns: 1fr; }
  .kpi-secondary-grid  { grid-template-columns: 1fr; }
  .charts-grid-2       { grid-template-columns: 1fr; }
  .charts-grid-resumen { grid-template-columns: 1fr; }
  .charts-grid-3       { grid-template-columns: 1fr; }
}

/* ── Clasificación e IA ──────────────────────────────────── */
.clasif-intro {
  background: #f8fafc;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
  padding: 16px 20px;
}
.clasif-estado {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.73rem;
  font-weight: 600;
}
.clasif-estado--ok     { background: #d1fae5; color: #065f46; }
.clasif-estado--warn   { background: #fef3c7; color: #92400e; }
.clasif-estado--danger { background: #fee2e2; color: #991b1b; }

/* ── Monitoreo de Ventas ─────────────────────────────────── */
.charts-grid-4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.mon-intro-card {
  border-left: 4px solid #3B82F6 !important;
}
.mon-kpi-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 18px 20px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
}
.mon-kpi-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.mon-kpi-card__label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: #6b7280;
}
.mon-kpi-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 1rem;
  flex-shrink: 0;
}
.mon-kpi-card__value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}
.mon-kpi-card__sub {
  font-size: 0.75rem;
  color: #9ca3af;
}
@media (max-width: 1024px) {
  .charts-grid-4 { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .charts-grid-4 { grid-template-columns: 1fr; }
}

/* ── Fidelidad y RFM ──────────────────────────────────────── */
.rfm-intro-card { border-left: 4px solid #00b8d4 !important; }

/* Fila superior: scatter 3D (2/3) + donut (1/3) */
.rfm-top-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
}
.rfm-scatter-card { display: flex; flex-direction: column; }
.rfm-3d-wrap {
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  flex: 1;
  min-height: 240px;
  padding: 8px;
  display: flex; align-items: center; justify-content: center;
}
.rfm-3d-canvas {
  cursor: grab;
  width: 100%;
  height: auto;
  display: block;
}
.rfm-3d-canvas:active { cursor: grabbing; }

.rfm-zoom-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 4px 2px;
}
.rfm-zoom-label {
  display: flex; align-items: center; gap: 4px;
  font-size: 0.68rem; font-weight: 700; color: #9ca3af;
  text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap;
}
.rfm-zoom-slider {
  flex: 1; height: 4px; border-radius: 4px;
  appearance: none; -webkit-appearance: none;
  background: #e2e8f0; cursor: pointer; accent-color: #00b8d4;
}
.rfm-zoom-val {
  font-size: 0.72rem; font-weight: 700; font-family: monospace;
  background: #f1f5f9; color: #475569;
  padding: 2px 8px; border-radius: 6px;
  min-width: 42px; text-align: center;
}

.rfm-donut-card { display: flex; flex-direction: column; }
.rfm-donut-wrap { height: 200px; position: relative; }

.rfm-criteria { border-top: 1px solid #f3f4f6; padding-top: 10px; }
.rfm-criteria-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 7px; font-size: 0.72rem;
}
.rfm-criteria-label { font-weight: 700; }
.rfm-criteria-label--activo   { color: #10b981; }
.rfm-criteria-label--riesgo   { color: #f59e0b; }
.rfm-criteria-label--inactivo { color: #ef4444; }
.rfm-criteria-val { color: #6b7280; }

/* Badges de estado en tabla */
.rfm-estado-badge {
  display: inline-block;
  padding: 3px 10px; border-radius: 12px;
  font-size: 0.73rem; font-weight: 600; white-space: nowrap;
}
.rfm-estado-badge--activo   { background: #d1fae5; color: #065f46; }
.rfm-estado-badge--riesgo   { background: #fef3c7; color: #92400e; }
.rfm-estado-badge--inactivo { background: #fee2e2; color: #991b1b; }

@media (max-width: 1024px) {
  .rfm-top-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .rfm-donut-wrap { height: 160px; }
}
</style>
